import React, { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { DirectorMappingState, Episode, Shot, ShotVersion, Resource, AssetRef, AnalysisCharacter, Project, VideoModel, VoiceExtractionTask, VoiceSwapTask, VoiceSwapMatchedSegment, SubtitleEraseTask, ResourceReviewItem, User, EpisodeTranslationSummary, ProjectAsset } from '../types';
import { StateOption } from '../components/AssetStateSelector';
import {
  getEpisode,
  getEpisodeContent,
  updateEpisode,
  generatePrompts,
  listShots,
  updateShot,
  generateVideo,
  listShotVersions,
  uploadResource,
  listResources,
  deleteResource,
  getScriptAnalysis,
  updateShotAssetRef,
  addShotAssetRef,
  removeShotAssetRef,
  rematchShotAssets,
  matchEpisodeAssetStates,
  getProject,
  cancelVideo,
  updateShotSelectedVersion,
  updateShotSecondaryVersions,
  createShot,
  duplicateShot,
  deleteShot,
  createShareLink,
  createRefResource,
  listAllEpisodeVersions,
  normalizeEpisode,
  resolvePronouns,
  regenerateAllPrompts,
  normalizeAllEpisodes,
  listEpisodes,
  getDownloadUrl,
  submitAssetReview,
  getAssetReviewStatus,
  AssetReviewItem,
  getResourceReviewStatus,
  submitResourceReview,
  getProjectResourceReviews,
  extractVoices,
  getVoiceExtraction,
  getExtractedVersionIds,
  getActiveExtractions,
  getSwapVersionStatuses,
  swapVoice,
  getVoiceSwap,
  applyCharacterVoice,
  submitSubtitleErase,
  getSubtitleEraseStatus,
  batchSubtitleEraseStatus,
  applyLanguageToShot,
  getLanguageSkill,
  getShot,
  getEpisodeTranslations,
  getEpisodeTranslation,
  getRefTranslationLanguages,
  startEpisodeTranslation,
  getProjectAssets,
} from '../api';
import EmptyState from '../components/EmptyState';
import { useToast } from '../components/Toast';
import { platformSupportsResourceReview } from '../utils/platformCapability';
import { tosAccelerate } from '../utils/tosAccelerate';
import { readImageFileDimensions, validateImageDimensions, validateImageAspectRatio, aspectRatioToastParams } from '../utils/imageDimensions';
import {
  INELIGIBLE_LABEL_KEYS,
  INELIGIBLE_TITLE_KEYS,
  IneligibleReason,
} from './episode-detail/constants';
import { AnalysisAssetItem, AnalysisAssetsState, AssetLookup, AssetPickerTabKey, AssetPickerTab } from './episode-detail/types';
import {
  getPreviewableShots,
  getSelectedVersionId as resolveSelectedVersionId,
  getSelectedVideoURL as resolveSelectedVideoURL,
  isEpisodeClipReady,
} from './episode-detail/utils/videoSelection';
import { getAssetCategoryMap } from './episode-detail/utils/assetPicker';
import { findAssetLookupEntry, parseAnalysisAssets } from './episode-detail/utils/analysisAssets';
import { shouldPollEpisode } from './episode-detail/utils/episodePolling';
import { getAssetReviewSubmitToast } from './episode-detail/utils/assetReviewSubmitToast';
import { parseAssetRefs } from './episode-detail/utils/assetRefs';
import { getBlockingResourceReviewsForShot } from './episode-detail/utils/resourceReviewGate';
import { useProcessingPoll } from './episode-detail/hooks/useProcessingPoll';
import {
  clearPendingVideoSubmission,
  markPendingVideoSubmission,
  PendingVideoSubmissions,
} from './episode-detail/utils/videoSubmitLock';
import { runVideoSubmitFlow, VideoSubmitContext } from './episode-detail/utils/videoSubmitFlow';
import { saveThenGenerate } from './episode-detail/utils/saveThenGenerate';
import ClipCaptureModal, { ClipCaptureTarget } from './episode-detail/components/ClipCaptureModal';
import EpisodePreviewPlayer from './episode-detail/components/EpisodePreviewPlayer';
import ReviewStatusPanel from './episode-detail/components/ReviewStatusPanel';
import ShotNavigationDock from './episode-detail/components/ShotNavigationDock';
import { computeShotHighlights, shallowEqualWeights } from './episode-detail/shotHighlight';
import type { ShotRect } from './episode-detail/shotHighlight';
import ShotListHeader from './episode-detail/components/ShotListHeader';
import EpisodeContentCard from './episode-detail/components/EpisodeContentCard';
import EpisodePageHeader from './episode-detail/components/EpisodePageHeader';
import HeaderProjectNav from '../components/HeaderProjectNav';
import { useHeaderSlot } from '../components/HeaderSlotContext';
import { useProjectConfigState } from './project-detail/hooks/useProjectConfigState';
import { loadActiveVideoModels } from './project-detail/hooks/useProjectBootstrapOptions';
import EditProjectDialog from './project-detail/components/EditProjectDialog';
import StyleConfigDialog from './project-detail/components/StyleConfigDialog';
import CostFloatingPill from '../components/CostFloatingPill';
import EpisodeNavSidebar from './episode-detail/components/EpisodeNavSidebar';
import EpisodeSummaryCard from './episode-detail/components/EpisodeSummaryCard';
import EpisodeScriptPeek, { ScriptPeekMemory } from './episode-detail/components/EpisodeScriptPeek';
import MobileAssetDrawer from '../components/MobileAssetDrawer';
import ConfirmDialog from '../components/ConfirmDialog';
import StatusFlash from '../components/StatusFlash';
import { useIsMobile } from '../utils/useIsMobile';
import { useRegistry } from '../utils/useRegistry';
import EpisodeFooterNav from './episode-detail/components/EpisodeFooterNav';
import ShotInsertZone from './episode-detail/components/ShotInsertZone';
import ShotHeader from './episode-detail/components/ShotHeader';
import DirectorMappingControls from './episode-detail/components/DirectorMappingControls';
import AIClipControls from './episode-detail/components/AIClipControls';
import { MediaKind } from '../utils/assetTokens';
import { LimitKind, addCounts, countKinds, countShotRefs, firstExceededLimit, resolveVideoLimits } from '../utils/videoLimits';
import ShotDetails from './episode-detail/components/ShotDetails';
import ShotCommentModal from './episode-detail/components/ShotCommentModal';
import ShotVersionPreview, { VideoLoadPlaceholder } from './episode-detail/components/ShotVersionPreview';
import { cssAspectRatio } from './episode-detail/utils/cssAspectRatio';
import ShotVersionDetailPanel from './episode-detail/components/ShotVersionDetailPanel';
import ShotVersionHeader from './episode-detail/components/ShotVersionHeader';
import SubtitleEraseStatusNotice from './episode-detail/components/SubtitleEraseStatusNotice';
import EpisodeDetailModals from './episode-detail/components/EpisodeDetailModals';
import type { PreviewAsset } from './episode-detail/components/AssetPreviewLightbox';
import { projectEpisodePath } from '../utils/routes';
import { collapseCharactersWithVoice } from '../utils/characterVoice';
import { assetPickerAdditionKinds, findActiveReviewForAsset } from './episode-detail/utils/assetPickerAdditionKinds';

const LIMIT_TOAST_SUFFIX: Record<LimitKind, string> = {
  images: 'maxImages',
  videos: 'maxVideos',
  audios: 'maxAudios',
  total: 'maxTotal',
};

const EpisodeDetail: React.FC = () => {
  const { t } = useTranslation('episodeDetail');
  const { id, projectId: routeProjectId } = useParams<{ id: string; projectId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const ineligibleLabel = (reason: IneligibleReason): string => t(INELIGIBLE_LABEL_KEYS[reason]);
  const ineligibleTitle = (reason: IneligibleReason): string => t(INELIGIBLE_TITLE_KEYS[reason]);
  const [episode, setEpisode] = useState<Episode | null>(null);
  const [projectName, setProjectName] = useState('');
  const [content, setContent] = useState<string>('');
  const [shots, setShots] = useState<Shot[]>([]);
  const [directorMappingRefreshToken, setDirectorMappingRefreshToken] = useState(0);
  const shotsRef = useRef<Shot[]>([]);
  const shotsRouteEpisodeIdRef = useRef(id || '');
  const activeEpisodeIdRef = useRef(id || '');
  const shotPresentationEpochRef = useRef(0);
  const shotLoadRequestRef = useRef(0);
  activeEpisodeIdRef.current = id || '';
  if (shotsRouteEpisodeIdRef.current !== activeEpisodeIdRef.current) {
    shotsRouteEpisodeIdRef.current = activeEpisodeIdRef.current;
    shotsRef.current = [];
    shotPresentationEpochRef.current += 1;
    shotLoadRequestRef.current += 1;
  }
  const isActiveEpisodeScope = useCallback(
    (episodeId: string) => episodeId !== '' && activeEpisodeIdRef.current === episodeId,
    [],
  );
  const getActiveOwnedShot = useCallback((shotId: string): Shot | null => {
    const target = shotsRef.current.find(shot => shot.id === shotId);
    return target && target.episodeId === activeEpisodeIdRef.current ? target : null;
  }, []);
  useLayoutEffect(() => {
    shotsRef.current = [];
    setShots([]);
  }, [id]);
  useEffect(() => {
    shotsRef.current = shots.filter(shot => shot.episodeId === activeEpisodeIdRef.current);
  }, [shots]);

  // 记住本项目最后浏览的剧集，供资产首页的「分镜列表」toggle 回跳。
  useEffect(() => {
    if (episode?.projectId && episode?.id) {
      localStorage.setItem(`lastEpisode:${episode.projectId}`, episode.id);
    }
  }, [episode?.projectId, episode?.id]);
  // 本集梗概（来自 script-summary 的 registry，按集号匹配）
  const { data: registry } = useRegistry(episode?.projectId);
  const episodeSummary = useMemo(
    () => registry?.episodes.find(e => e.episode === episode?.index) ?? null,
    [registry, episode?.index],
  );
  const [loading, setLoading] = useState(true);
  const [generatingPrompts, setGeneratingPrompts] = useState(false);
  const [polling, setPolling] = useState(false);
  const [analysisCompleted, setAnalysisCompleted] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [confirmRematchAssets, setConfirmRematchAssets] = useState(false);
  const [confirmDeleteResource, setConfirmDeleteResource] = useState<{ resourceId: string; shotId: string } | null>(null);
  const [deleteShotTarget, setDeleteShotTarget] = useState<{ id: string; index: number } | null>(null);
  const [deleteShotStep, setDeleteShotStep] = useState<1 | 2>(1);
  const [duplicateShotTarget, setDuplicateShotTarget] = useState<Shot | null>(null);
  const [duplicatingShotLoading, setDuplicatingShotLoading] = useState(false);
  const [deletingShotLoading, setDeletingShotLoading] = useState(false);

  // 剧集内容 tab
  const [contentTab, setContentTab] = useState<string>('original');
  const [originalContent, setOriginalContent] = useState<string>('');
  const [originalContentLoaded, setOriginalContentLoaded] = useState(false);
  const [normalizedContent, setNormalizedContent] = useState<string>('');
  const [normalizedContentLoaded, setNormalizedContentLoaded] = useState(false);
  // 参考翻译：summary 列表 + 各语言已加载内容（懒加载，点击 tab 时拉取）——与剧本信息页分集弹窗一致。
  const [translations, setTranslations] = useState<EpisodeTranslationSummary[]>([]);
  const [transContent, setTransContent] = useState<Record<string, string>>({});
  const [presetLangs, setPresetLangs] = useState<string[]>([]);
  const transPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [normalizingEpisode, setNormalizingEpisode] = useState(false);
  const [resolvingPronouns, setResolvingPronouns] = useState(false);
  // 「本集梗概」浮泡剧本视图的浏览位置记忆（tab + 各 tab 滚动位置）：同一集内复用，切集重建。
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const scriptPeekMemory = useMemo<ScriptPeekMemory>(() => ({ scroll: {} }), [id]);
  const [projectData, setProjectData] = useState<Project | null>(null);
  const [videoModels, setVideoModels] = useState<VideoModel[]>([]);
  // 项目所选视频模型的生成约束。resolved=false（项目/模型列表未加载、加载失败或匹配不到）
  // 时调用方必须放行，后端是唯一权威闸口。
  const videoLimits = useMemo(
    () => resolveVideoLimits(videoModels, projectData),
    [videoModels, projectData],
  );

  // 顶栏「项目设置/风格设置/协作者」按钮 + 配置弹窗（弹窗在本页渲染，nav 只放按钮）
  const currentUser: User | null = JSON.parse(localStorage.getItem('user') || 'null');
  const [editingProject, setEditingProject] = useState(false);
  const [styleConfigOpen, setStyleConfigOpen] = useState(false);
  const refreshProject = useCallback(() => {
    if (projectData?.id) getProject(projectData.id).then(p => { setProjectName(p.name); setProjectData(p); }).catch(err => {
      console.error('Failed to refresh project, generation limits may stay on stale values:', err);
    });
  }, [projectData?.id]);
  const projectCfg = useProjectConfigState(projectData, { onSaved: refreshProject });
  // 把面包屑 + 页面切换 toggle + 配置按钮 + 总消耗注入顶部导航栏。
  useHeaderSlot(
    episode
      ? <HeaderProjectNav
          projectId={episode.projectId}
          active="storyboard"
          project={projectData ?? undefined}
          currentUser={currentUser}
          memberCount={projectData?.members?.length ?? 0}
          onEditProject={() => setEditingProject(true)}
          onStyleConfig={() => setStyleConfigOpen(true)}
        />
      : null,
    episode ? <CostFloatingPill projectId={episode.projectId} variant="inline" /> : null,
    [episode?.projectId, !!projectData, projectData?.members?.length],
  );

  // 语言技能改写状态
  const [warnShot, setWarnShot] = useState<Shot | null>(null);
  const [languageSkillName, setLanguageSkillName] = useState<string>('');
  const [applyingLanguage, setApplyingLanguage] = useState<string | null>(null); // shotId being applied

  // 上一集/下一集导航
  const [prevEpisodeId, setPrevEpisodeId] = useState<string | null>(null);
  const [nextEpisodeId, setNextEpisodeId] = useState<string | null>(null);
  const [episodeCount, setEpisodeCount] = useState(0);
  // 左侧剧集导航栏（从资产页搬来的分集面板）
  const isMobile = useIsMobile();
  const [episodeList, setEpisodeList] = useState<Episode[]>([]);
  const [episodeListPolling, setEpisodeListPolling] = useState(false);
  const [analysisStatus, setAnalysisStatus] = useState<string | undefined>(undefined);
  const [episodeDrawerOpen, setEpisodeDrawerOpen] = useState(false);
  const [confirmRegenAllOpen, setConfirmRegenAllOpen] = useState(false);
  const [confirmNormalizeAllOpen, setConfirmNormalizeAllOpen] = useState(false);
  const [confirmRetryFailedOpen, setConfirmRetryFailedOpen] = useState(false);
  const [regenAllLoading, setRegenAllLoading] = useState(false);
  const [normalizeAllLoading, setNormalizeAllLoading] = useState(false);
  const [retryFailedLoading, setRetryFailedLoading] = useState(false);

  // 左侧分镜导航：当前可视分镜 id
  const [activeShotId, setActiveShotId] = useState<string | null>(null);
  const [shotWeights, setShotWeights] = useState<Record<string, number>>({});
  const prevPrimaryRef = useRef<string | null>(null);
  const prevWeightsRef = useRef<Record<string, number>>({});

  // 剧集内容编辑
  const [editingContent, setEditingContent] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [savingContent, setSavingContent] = useState(false);
  const [showNormalizePrompt, setShowNormalizePrompt] = useState(false);

  // 分镜展开/编辑
  const [collapsedShots, setCollapsedShots] = useState<Set<string>>(new Set());
  // 历史条视频按需加载：只有点开的版本才挂 <video>，避免版本多时 DOM 里堆几百个视频卡死页面
  const [activatedVersions, setActivatedVersions] = useState<Set<string>>(new Set());
  const activateVersion = useCallback((versionId: string) => {
    setActivatedVersions(prev => {
      const next = new Set(prev);
      next.add(versionId);
      return next;
    });
  }, []);
  const [editingShot, setEditingShot] = useState<string | null>(null);
  const [shotEdits, setShotEdits] = useState<{ duration: number; description: string; remark: string; prompt: string }>({ duration: 0, description: '', remark: '', prompt: '' });
  const [savingShot, setSavingShot] = useState(false);
  const [pendingVideoSubmissions, setPendingVideoSubmissions] = useState<PendingVideoSubmissions>({});
  const pendingVideoSubmissionTokensRef = useRef<Record<string, symbol>>({});
  const videoSubmissionScopeRef = useRef(0);
  const [cancelingVideo, setCancelingVideo] = useState<string | null>(null); // versionId
  const [cancelFailedTaskIds, setCancelFailedTaskIds] = useState<Set<string>>(new Set()); // external videoTaskIds
  const [rematchingAssets, setRematchingAssets] = useState<string | null>(null);
  const [rematchingEpisodeAssets, setRematchingEpisodeAssets] = useState(false);
  const [assetMatchPolling, setAssetMatchPolling] = useState(false);

  useEffect(() => {
    videoSubmissionScopeRef.current += 1;
    pendingVideoSubmissionTokensRef.current = {};
    setPendingVideoSubmissions({});

    return () => {
      videoSubmissionScopeRef.current += 1;
      pendingVideoSubmissionTokensRef.current = {};
    };
  }, [id]);

  // 人物审核状态
  const [assetReviewRequired, setAssetReviewRequired] = useState(false);
  const [assetReviews, setAssetReviews] = useState<AssetReviewItem[]>([]);
  const [assetReviewAllActive, setAssetReviewAllActive] = useState(false);
  const [reviewPanelCollapsed, setReviewPanelCollapsed] = useState(false);
  const prevAllActiveRef = useRef<boolean | null>(null);
  const [submittingReview, setSubmittingReview] = useState(false);

  // 插入分镜
  const [insertAfterIndex, setInsertAfterIndex] = useState<number | null>(null);
  const [insertShotData, setInsertShotData] = useState({ duration: 5, prompt: '' });
  const [insertingShotLoading, setInsertingShotLoading] = useState(false);

  // 资源审核（真人剧分镜资源）
  const [resourceReviews, setResourceReviews] = useState<ResourceReviewItem[]>([]);
  const [, setResourceReviewAllActive] = useState(true);
  const [resourceReviewRequired, setResourceReviewRequired] = useState(false);
  const [submittingResourceReview, setSubmittingResourceReview] = useState(false);
  const [humanContentDialogOpen, setHumanContentDialogOpen] = useState(false);
  const [, setPendingUploadShotId] = useState<string | null>(null);
  const pendingUploadShotIdRef = useRef<string | null>(null);
  const [, setPendingContainsHuman] = useState(false);
  const pendingContainsHumanRef = useRef(false);
  const [, setPendingResourceName] = useState('');
  const pendingResourceNameRef = useRef('');
  const [projectActiveReviews, setProjectActiveReviews] = useState<ResourceReviewItem[]>([]);

  // 资源
  const [shotResources, setShotResources] = useState<Record<string, Resource[]>>({});
  const [uploadingResource, setUploadingResource] = useState<string | null>(null);

  // 引用资产状态切换（Modal 选择器）
  const [stateSelectorData, setStateSelectorData] = useState<{
    shotId: string;
    imgIndex: number;
    assetName: string;
    assetType: 'character' | 'scene' | 'prop' | null;
    allStates: StateOption[];
    currentState: string;
    currentImageUrl: string;
  } | null>(null);
  const [previewAsset, setPreviewAsset] = useState<PreviewAsset | null>(null);
  // 本地 assetRefs 覆盖（切换状态后更新）
  const [shotAssetRefs, setShotAssetRefs] = useState<Record<string, AssetRef[]>>({});
  // 分析资产选择器
  const [analysisAssets, setAnalysisAssets] = useState<AnalysisAssetsState>({ characters: [], scenes: [], props: [] });
  // 资产数据源查找表：name → { state → selectedImageUrl, allStates }
  const [assetLookup, setAssetLookup] = useState<AssetLookup>(new Map());
  const [pickingAssetFor, setPickingAssetFor] = useState<string | null>(null); // shotId
  const [pickerTab, setPickerTab] = useState<AssetPickerTabKey>('characters');
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState<AnalysisAssetItem[]>([]);
  const [addingAssets, setAddingAssets] = useState(false);
  // 视频截取（单帧/片段）弹窗
  const [clipTarget, setClipTarget] = useState<ClipCaptureTarget | null>(null);
  const [projectAssets, setProjectAssets] = useState<ProjectAsset[]>([]);
  const [librarySelectedIds, setLibrarySelectedIds] = useState<string[]>([]);
  const [addingVideoRef, setAddingVideoRef] = useState(false);

  // 分镜批注弹窗
  const [commentShotId, setCommentShotId] = useState<string | null>(null);
  const [commentShotIndex, setCommentShotIndex] = useState(0);

  // 版本历史（兼视频生成记录）
  const [shotVersions, setShotVersions] = useState<Record<string, ShotVersion[]>>({});
  const [showVersionDetail, setShowVersionDetail] = useState<Record<string, boolean>>({});
  const [playedVideos, setPlayedVideos] = useState<Set<string>>(() => {
    try { const s = localStorage.getItem(`video-played:${id}`); return s ? new Set(JSON.parse(s)) : new Set(); } catch { return new Set(); }
  });
  const playedSeededRef = useRef(false);
  useEffect(() => {
    if (playedSeededRef.current || !id) return;
    if (localStorage.getItem(`video-played:${id}`) !== null) { playedSeededRef.current = true; return; }
    const existing: string[] = [];
    Object.values(shotVersions).forEach(versions => {
      versions.forEach(v => { if (v.taskStatus === 'completed' && v.videoURL) existing.push(v.id); });
    });
    if (existing.length === 0) return;
    playedSeededRef.current = true;
    localStorage.setItem(`video-played:${id}`, JSON.stringify(existing));
    setPlayedVideos(new Set(existing));
  }, [shotVersions, id]);
  const markVideoPlayed = useCallback((versionId: string) => {
    setPlayedVideos(prev => {
      if (prev.has(versionId)) return prev;
      const next = new Set(prev);
      next.add(versionId);
      localStorage.setItem(`video-played:${id}`, JSON.stringify(Array.from(next)));
      return next;
    });
  }, [id]);

  // 视频预览拼接播放（双缓冲）
  const [previewExpanded, setPreviewExpanded] = useState(false);
  const [previewMounted, setPreviewMounted] = useState(false);
  const [selectedVideoVersions, setSelectedVideoVersions] = useState<Record<string, string>>({}); // shotId -> versionId
  const [secondaryVersions, setSecondaryVersions] = useState<Record<string, string[]>>({}); // shotId -> versionId[]
  const [previewCurrentIndex, setPreviewCurrentIndex] = useState(0);
  const [, setPreviewPlaying] = useState(false);
  const [activeBuffer, setActiveBuffer] = useState<'A' | 'B'>('A');
  const videoRefA = useRef<HTMLVideoElement>(null);
  const videoRefB = useRef<HTMLVideoElement>(null);
  const activeBufferRef = useRef<'A' | 'B'>('A'); // 同步 ref，避免闭包问题
  const nextPreloadedUrlRef = useRef<string>(''); // 后台 buffer 已预加载的 URL
  const [switchLoading, setSwitchLoading] = useState(false); // 切换时等待加载
  const [initialReady, setInitialReady] = useState(false); // 首个视频是否就绪
  const thumbnailStripRef = useRef<HTMLDivElement>(null);
  const scrolledShotsRef = useRef<Set<string>>(new Set()); // 避免重复滚动
  const [shareModal, setShareModal] = useState(false);
  const [shareForm, setShareForm] = useState({ hours: 12, maxViews: 0 });
  const [shareLoading, setShareLoading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState<{ current: number; total: number } | null>(null);

  // 音色提取 & 换音色
  const [extractionModal, setExtractionModal] = useState<{ shotId: string; versionId: string } | null>(null);
  const [extractionTask, setExtractionTask] = useState<VoiceExtractionTask | null>(null);
  const [extractionLoading, setExtractionLoading] = useState(false);
  const [applyingVoice, setApplyingVoice] = useState<string | null>(null); // characterName being applied
  const [appliedVoices, setAppliedVoices] = useState<Set<string>>(new Set());
  const [extractedVersionIds, setExtractedVersionIds] = useState<Set<string>>(new Set());
  // 进行中（pending/processing/waiting）的提取任务版本集合：跨弹窗关闭仍保留，
  // 用于禁用按钮，防止重复点击重复发任务（后端亦有幂等守卫兜底）。
  const [extractingVersionIds, setExtractingVersionIds] = useState<Set<string>>(new Set());
  const [swapModal, setSwapModal] = useState<{ shotId: string; versionId: string } | null>(null);
  const [swapTask, setSwapTask] = useState<Pick<VoiceSwapTask, 'status' | 'replacedVocalUrl' | 'bgmUrl' | 'errorMessage' | 'matchedSegments'> | null>(null);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapCharacters, setSwapCharacters] = useState<string[]>([]);
  const [analysisChars, setAnalysisChars] = useState<AnalysisCharacter[]>([]);
  const [swapVersionStatus, setSwapVersionStatus] = useState<Record<string, { status: string; taskId?: string; replacedVocalUrl?: string; bgmUrl?: string; matchedSegments?: VoiceSwapMatchedSegment[]; errorMessage?: string }>>({});
  const swapPollRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const [voiceError, setVoiceError] = useState<Record<string, string>>({}); // versionId -> error message
  const extractionPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // 刷新后回填"提取中"的后台恢复轮询（按 versionId 多路并行，不碰弹窗 extractionTask）
  const resumeExtractionPollRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const startExtractionResumeRef = useRef<(taskId: string, versionId: string) => void>(() => {});
  const promptGenForcePollRef = useRef(false);

  // 字幕擦除
  const [subtitleEraseStatus, setSubtitleEraseStatus] = useState<Record<string, SubtitleEraseTask>>({});
  const erasePollRefs = useRef<Record<string, ReturnType<typeof setInterval>>>({});
  const startSwapPollRef = useRef<(versionId: string, taskId: string) => void>(() => {});
  const startErasePollRef = useRef<(versionId: string) => void>(() => {});

  useEffect(() => {
    const swapPolls = swapPollRefs.current;
    const erasePolls = erasePollRefs.current;
    const resumePolls = resumeExtractionPollRefs.current;
    return () => {
      if (extractionPollRef.current) clearInterval(extractionPollRef.current);
      Object.values(swapPolls).forEach(id => clearInterval(id));
      Object.values(erasePolls).forEach(id => clearInterval(id));
      Object.values(resumePolls).forEach(id => clearInterval(id));
    };
  }, []);

  // 重新拉取剧本要素分析并刷新资产查找表（assetLookup 是分镜资产图实时解析的数据源，
  // 用户在资产设定中更换候选图后必须刷新它，否则 getAssetRefs 会用旧图覆盖新 URL）
  const refreshAnalysisAssets = useCallback(async (projectId: string) => {
    const analysis = await getScriptAnalysis(projectId);
    setAnalysisStatus(analysis.status);
    const completed = analysis.status === 'completed' || analysis.status === 'extracted';
    setAnalysisCompleted(completed);
    if (completed) {
      const { assets, lookup } = parseAnalysisAssets(analysis);
      setAnalysisAssets(assets);
      setAssetLookup(lookup);
    }
  }, []);

  const loadEpisode = useCallback(async (episodeId: string) => {
    try {
      const data = await getEpisode(episodeId);
      setEpisode(data);
      if (data.projectId && routeProjectId !== data.projectId) {
        navigate(projectEpisodePath(data.projectId, data.id, location.hash), { replace: true });
      }
      const shouldPoll = shouldPollEpisode(data);
      if (shouldPoll) promptGenForcePollRef.current = false;
      setPolling(shouldPoll || promptGenForcePollRef.current);
      // 资产匹配状态同步
      const matchStatus = data.assetMatchStatus || 'idle';
      const isMatching = matchStatus === 'matching';
      setAssetMatchPolling(isMatching);
      if (isMatching) {
        setRematchingEpisodeAssets(true);
      } else {
        if (matchStatus === 'completed' || matchStatus === 'failed') {
          // 后端已完成/失败，清除 localStorage 和前端 loading
          const lsKey = `assetMatch_${episodeId}`;
          if (localStorage.getItem(lsKey)) {
            localStorage.removeItem(lsKey);
            if (data.assetMatchStatus === 'completed') {
              toast.success(t('toast.assetMatchCompleted'));
            } else {
              toast.error(data.assetMatchError || t('toast.assetMatchFailed'));
            }
          }
        }
        // 非 matching 状态(含 idle)都必须重置 loading,
        // 否则从正在匹配的剧集切到 idle 剧集时,上一集的 true 会残留导致假“匹配中”
        setRematchingEpisodeAssets(false);
      }
      getProject(data.projectId).then(p => { setProjectName(p.name); setProjectData(p); }).catch(err => {
        console.error('Failed to load project, generation limits will not be enforced client-side:', err);
      });
      loadActiveVideoModels().then(setVideoModels).catch(err => {
        console.error('Failed to load active video models, generation limits will not be enforced client-side:', err);
      });
      // 拉取剧本要素分析状态
      try {
        await refreshAnalysisAssets(data.projectId);
      } catch {
        setAnalysisCompleted(false);
      }
    } catch (err) {
      console.error('Failed to load episode:', err);
    }
  }, [location.hash, navigate, routeProjectId, refreshAnalysisAssets, t, toast]);

  const loadShots = useCallback(async (episodeId: string) => {
    const presentationEpoch = shotPresentationEpochRef.current;
    const requestId = ++shotLoadRequestRef.current;
    const isCurrentLoad = () => (
      activeEpisodeIdRef.current === episodeId
      && shotPresentationEpochRef.current === presentationEpoch
      && shotLoadRequestRef.current === requestId
    );
    try {
      const data = await listShots(episodeId);
      if (!isCurrentLoad()) return;
      setShots(data);
      // 初始化已保存的视频版本选择
      const saved: Record<string, string> = {};
      const savedSecondary: Record<string, string[]> = {};
      data.forEach(shot => {
        if (shot.selectedVersionId) saved[shot.id] = shot.selectedVersionId;
        if (shot.secondaryVersionIds) {
          try {
            const ids = JSON.parse(shot.secondaryVersionIds);
            if (Array.isArray(ids)) savedSecondary[shot.id] = ids;
          } catch { /* ignore */ }
        }
      });
      setSelectedVideoVersions(prev => ({ ...saved, ...prev }));
      setSecondaryVersions(prev => ({ ...savedSecondary, ...prev }));
      // 批量加载所有分镜的版本历史（单次请求）
      try {
        const allVersions = await listAllEpisodeVersions(episodeId);
        if (!isCurrentLoad()) return;
        setShotVersions(allVersions);
      } catch (verErr) {
        console.error('Failed to batch load versions, falling back:', verErr);
        data.forEach(shot => loadVersions(shot.id));
      }
      // 预加载所有分镜的资源（默认全部展开）
      Promise.all(data.map(shot => listResources(shot.id).catch(() => [] as Resource[])))
        .then(results => {
          if (!isCurrentLoad()) return;
          const resourceMap: Record<string, Resource[]> = {};
          data.forEach((shot, i) => { resourceMap[shot.id] = results[i]; });
          setShotResources(prev => ({ ...resourceMap, ...prev }));
        });
    } catch (err) {
      console.error('Failed to load shots:', err);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalidateDirectorPresentation = useCallback((episodeId: string) => {
    if (!isActiveEpisodeScope(episodeId)) return;
    shotPresentationEpochRef.current += 1;
    shotLoadRequestRef.current += 1;
    setShots(previous => previous.map(shot => ({
      ...shot,
      directorLabels: [],
      directorNotes: [],
    })));
    setDirectorMappingRefreshToken(previous => previous + 1);
  }, [isActiveEpisodeScope]);

  const handleDirectorMappingStateChange = useCallback((state: DirectorMappingState) => {
    if (state.currentResult) return;
    setShots(previous => previous.map(shot => (
      (shot.directorLabels?.length || shot.directorNotes?.length)
        ? { ...shot, directorLabels: [], directorNotes: [] }
        : shot
    )));
  }, []);

  const handleDirectorMappingTerminal = useCallback(async (state: DirectorMappingState) => {
    if (state.latestAttempt && id) {
      await loadShots(id);
    }
  }, [id, loadShots]);

  const loadContent = useCallback(async (episodeId: string) => {
    try {
      const data = await getEpisodeContent(episodeId, 'annotated'); // 默认加载代词标注版
      setContent(data.content);
    } catch (err) {
      console.error('Failed to load content:', err);
    }
  }, []);

  const loadOriginalContent = useCallback(async (episodeId: string) => {
    if (originalContentLoaded) return;
    try {
      const data = await getEpisodeContent(episodeId);
      setOriginalContent(data.content);
      setOriginalContentLoaded(true);
    } catch (err) {
      console.error('Failed to load original content:', err);
    }
  }, [originalContentLoaded]);

  const loadNormalizedContent = useCallback(async (episodeId: string) => {
    if (normalizedContentLoaded) return;
    try {
      const data = await getEpisodeContent(episodeId, 'normalized');
      setNormalizedContent(data.content);
      setNormalizedContentLoaded(true);
    } catch (err) {
      console.error('Failed to load normalized content:', err);
    }
  }, [normalizedContentLoaded]);

  const loadTranslations = useCallback(async () => {
    if (!id) return;
    try {
      const r = await getEpisodeTranslations(id);
      setTranslations(r.translations || []);
    } catch (err) { console.error('Failed to load episode translations:', err); }
  }, [id]);

  // 进入 / 切换分集时拉取一次翻译 summary。
  useEffect(() => { loadTranslations(); }, [loadTranslations]);

  // 任一语言 translating 时每 4s 轮询；无 translating 则停；卸载清理。
  useEffect(() => {
    const hasTranslating = translations.some(tr => tr.status === 'translating');
    if (hasTranslating && !transPollRef.current) {
      transPollRef.current = setInterval(loadTranslations, 4000);
    }
    if (!hasTranslating && transPollRef.current) {
      clearInterval(transPollRef.current); transPollRef.current = null;
    }
    return () => {
      if (transPollRef.current) { clearInterval(transPollRef.current); transPollRef.current = null; }
    };
  }, [translations, loadTranslations]);

  const loadTranslationContent = useCallback(async (language: string) => {
    if (!id || transContent[language] !== undefined) return;
    try {
      const d = await getEpisodeTranslation(id, language);
      if (d.status === 'completed' && d.content) {
        setTransContent(prev => ({ ...prev, [language]: d.content as string }));
      }
    } catch (err) { console.error('Failed to load translation content:', err); }
  }, [id, transContent]);

  // 拉取一次预设参考语言列表（用于「开始翻译」下拉）。
  useEffect(() => {
    getRefTranslationLanguages()
      .then(r => setPresetLangs(r.languages || []))
      .catch(err => console.error('Failed to load preset languages:', err));
  }, []);

  // 尚无翻译记录的预设语言（可手动发起翻译）。
  const startableLanguages = presetLangs.filter(l => !translations.some(tr => tr.language === l));

  // 手动发起某语言翻译：乐观插入 translating tab（触发轮询）并切换过去，再向服务端发起。
  const handleStartTranslation = useCallback(async (language: string) => {
    if (!id || !language) return;
    setTranslations(prev => prev.some(tr => tr.language === language) ? prev : [...prev, { language, status: 'translating' }]);
    setContentTab(`trans:${language}`);
    try {
      await startEpisodeTranslation(id, language);
      loadTranslations(); // 与服务端对齐（并经现有 effect 重启轮询）
    } catch (err) {
      console.error('Failed to start translation:', err);
      loadTranslations();
    }
  }, [id, loadTranslations]);

  const handleNormalize = useCallback(async () => {
    if (!id) return;
    setNormalizingEpisode(true);
    try {
      await normalizeEpisode(id);
      toast.success(t('normalizePrompt.normalizeSubmitted'));
      setNormalizedContentLoaded(false);
      setPolling(true);
      loadEpisode(id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('normalizePrompt.normalizeFailed'));
    } finally {
      setNormalizingEpisode(false);
    }
  }, [id, t, toast, loadEpisode]);

  const handleResolvePronouns = useCallback(async () => {
    if (!id) return;
    setResolvingPronouns(true);
    try {
      await resolvePronouns(id);
      toast.success(t('normalizePrompt.resolveSubmitted'));
      setPolling(true);
      loadEpisode(id);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('normalizePrompt.resolveFailed'));
    } finally {
      setResolvingPronouns(false);
    }
  }, [id, t, toast, loadEpisode]);

  // 自动选择最佳内容 tab：代词标注版 > 改写版 > 原文
  useEffect(() => {
    if (episode?.annotatedContentURL) {
      setContentTab('annotated');
    } else if (episode?.normalizedContentURL && id) {
      setContentTab('normalized');
      loadNormalizedContent(id);
    } else {
      setContentTab('original');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [episode?.normalizedContentURL, episode?.annotatedContentURL]);

  // 页面加载时检查 localStorage 恢复匹配状态（10 分钟超时兜底）
  useEffect(() => {
    if (!id) return;
    const ts = localStorage.getItem(`assetMatch_${id}`);
    if (ts && Date.now() - Number(ts) < 10 * 60 * 1000) {
      setRematchingEpisodeAssets(true);
    } else if (ts) {
      localStorage.removeItem(`assetMatch_${id}`);
    }
  }, [id]);

  // 切换分集时重置缓存状态
  useEffect(() => {
    setOriginalContent('');
    setOriginalContentLoaded(false);
    setNormalizedContent('');
    setNormalizedContentLoaded(false);
    setContent('');
    setTranslations([]);
    setTransContent({});
    setCollapsedShots(new Set());
    setActivatedVersions(new Set());
    setShotResources({});
  }, [id]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      Promise.all([
        loadEpisode(id), loadShots(id), loadContent(id),
        getExtractedVersionIds(id).then(r => setExtractedVersionIds(new Set(r.versionIds || []))).catch(() => {}),
        // 刷新后回填"提取中"按钮态并恢复轮询（延迟执行，确保 startExtractionResume 已定义）
        getActiveExtractions(id).then(r => {
          const tasks = r.tasks || [];
          if (tasks.length === 0) return;
          setExtractingVersionIds(prev => {
            const next = new Set(prev);
            tasks.forEach(tk => next.add(tk.versionId));
            return next;
          });
          setTimeout(() => tasks.forEach(tk => startExtractionResumeRef.current(tk.taskId, tk.versionId)), 0);
        }).catch(() => {}),
        getSwapVersionStatuses(id).then(r => {
          const map: Record<string, { status: string; taskId?: string; replacedVocalUrl?: string; bgmUrl?: string; matchedSegments?: VoiceSwapMatchedSegment[]; errorMessage?: string }> = {};
          const pendingPolls: Array<{ versionId: string; taskId: string }> = [];
          for (const s of (r.statuses || [])) {
            map[s.versionId] = { status: s.status, taskId: s.taskId, replacedVocalUrl: s.replacedVocalUrl, bgmUrl: s.bgmUrl, matchedSegments: s.matchedSegments, errorMessage: s.errorMessage };
            if (s.status === 'pending' || s.status === 'processing' || s.status === 'waiting') {
              pendingPolls.push({ versionId: s.versionId, taskId: s.taskId });
            }
          }
          setSwapVersionStatus(map);
          // 恢复进行中任务的轮询（延迟执行，确保 startSwapPoll 已定义）
          setTimeout(() => pendingPolls.forEach(p => startSwapPollRef.current(p.versionId, p.taskId)), 0);
        }).catch(() => {}),
        // 批量加载字幕擦除状态
        listAllEpisodeVersions(id).then(versionMap => {
          const versionIds = Object.values(versionMap).flat().map(v => v.id);
          if (versionIds.length === 0) return;
          return batchSubtitleEraseStatus(versionIds).then(r => {
            setSubtitleEraseStatus(r.data || {});
            // 恢复 running 状态的轮询
            for (const [vid, task] of Object.entries(r.data || {})) {
              if (task.status === 'running') {
                setTimeout(() => startErasePollRef.current(vid), 0);
              }
            }
          });
        }).catch(() => {}),
      ]).finally(() => setLoading(false));
    }
  }, [id, loadEpisode, loadShots, loadContent]);

  // 剧集列表：驱动左侧导航栏 + 上一集/下一集；并据进行中状态决定是否轮询
  const loadEpisodeList = useCallback(async (projectId: string) => {
    try {
      const data = await listEpisodes(projectId);
      const sorted = [...data].sort((a, b) => a.index - b.index);
      setEpisodeList(sorted);
      setEpisodeCount(sorted.length);
      const idx = sorted.findIndex(e => e.id === id);
      setPrevEpisodeId(idx > 0 ? sorted[idx - 1].id : null);
      setNextEpisodeId(idx < sorted.length - 1 ? sorted[idx + 1].id : null);
      // 与单集自身轮询同源的 shouldPollEpisode 统一口径：pending 仅在 promptGenerationStatus==='generating'
      // 时才算进行中；终态失败不轮询（重试时各 handler 会显式重开轮询）。
      setEpisodeListPolling(sorted.some(shouldPollEpisode));
    } catch {
      // ignore
    }
  }, [id]);

  useEffect(() => {
    if (!episode?.projectId) return;
    loadEpisodeList(episode.projectId);
  }, [episode?.projectId, loadEpisodeList]);

  // 剧集列表轮询：有集在处理中时定时刷新，让侧栏状态实时更新
  useEffect(() => {
    if (!episodeListPolling || !episode?.projectId) return;
    const interval = setInterval(() => loadEpisodeList(episode.projectId), 5000);
    return () => clearInterval(interval);
  }, [episodeListPolling, episode?.projectId, loadEpisodeList]);

  // 侧栏顶部的批量操作（从资产页搬来）
  const handleRegenAllPrompts = async () => {
    const pid = episode?.projectId;
    if (!pid) return;
    const requestEpisodeId = activeEpisodeIdRef.current;
    setRegenAllLoading(true);
    try {
      await regenerateAllPrompts(pid);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      invalidateDirectorPresentation(requestEpisodeId);
      setConfirmRegenAllOpen(false);
      setEpisodeListPolling(true);
      loadEpisodeList(pid);
    } catch (err: any) {
      toast.error(err.message || t('confirmDialogs.regenAllPrompts.failed', { ns: 'projectDetail' }));
    } finally {
      setRegenAllLoading(false);
    }
  };
  const handleNormalizeAll = async () => {
    const pid = episode?.projectId;
    if (!pid) return;
    setNormalizeAllLoading(true);
    try {
      const res = await normalizeAllEpisodes(pid);
      setConfirmNormalizeAllOpen(false);
      toast.success(t('confirmDialogs.normalizeAll.success', { ns: 'projectDetail', count: res.triggered }));
      setEpisodeListPolling(true);
      loadEpisodeList(pid);
    } catch (err: any) {
      toast.error(err.message || t('confirmDialogs.normalizeAll.failed', { ns: 'projectDetail' }));
    } finally {
      setNormalizeAllLoading(false);
    }
  };
  const handleRetryFailedPrompts = async () => {
    const pid = episode?.projectId;
    if (!pid) return;
    const requestEpisodeId = activeEpisodeIdRef.current;
    const shouldInvalidate = episode?.promptGenerationStatus === 'failed';
    setRetryFailedLoading(true);
    try {
      const res = await regenerateAllPrompts(pid, true);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      if (shouldInvalidate) {
        invalidateDirectorPresentation(requestEpisodeId);
      }
      setConfirmRetryFailedOpen(false);
      toast.success(t('confirmDialogs.retryFailed.success', { ns: 'projectDetail', count: res.triggered }));
      setEpisodeListPolling(true);
      loadEpisodeList(pid);
    } catch (err: any) {
      toast.error(err.message || t('confirmDialogs.retryFailed.failed', { ns: 'projectDetail' }));
    } finally {
      setRetryFailedLoading(false);
    }
  };
  const handleRetryAllPronouns = async () => {
    const pid = episode?.projectId;
    if (!pid) return;
    const failed = episodeList.filter(ep => ep.status === 'resolving_pronouns_failed');
    if (failed.length === 0) return;
    try {
      await Promise.all(failed.map(ep => resolvePronouns(ep.id)));
      toast.success(t('episodesPanel.pronounRetrySuccess', { count: failed.length, defaultValue: `已重新提交 ${failed.length} 集格式改写` }));
      setEpisodeListPolling(true);
      loadEpisodeList(pid);
    } catch {
      toast.error(t('episodesPanel.pronounRetryFailed', { defaultValue: '重试格式改写失败' }));
    }
  };

  const episodeSidebar = episode && projectData ? (
    <EpisodeNavSidebar
      episodes={episodeList}
      currentEpisodeId={episode.id}
      project={projectData}
      analysisStatus={analysisStatus}
      onRegenAllPrompts={() => setConfirmRegenAllOpen(true)}
      onNormalizeAll={() => setConfirmNormalizeAllOpen(true)}
      onRetryFailed={() => setConfirmRetryFailedOpen(true)}
      onRetryPronouns={handleRetryAllPronouns}
      onNavigate={() => setEpisodeDrawerOpen(false)}
    />
  ) : null;

  useEffect(() => {
    if (!polling || !id) return;
    const interval = setInterval(() => {
      loadEpisode(id);
      loadShots(id);
    }, 2000);
    return () => clearInterval(interval);
  }, [polling, id, loadEpisode, loadShots]);

  // 获取语言技能名称（用于按钮/弹窗显示）
  useEffect(() => {
    if (!projectData?.languageSkillId) {
      setLanguageSkillName('');
      return;
    }
    let cancelled = false;
    getLanguageSkill(projectData.languageSkillId)
      .then(skill => { if (!cancelled) setLanguageSkillName(skill.name); })
      .catch(() => { if (!cancelled) setLanguageSkillName(''); });
    return () => { cancelled = true; };
  }, [projectData?.languageSkillId]);

  // 轮询 processing 状态的语言改写任务
  useEffect(() => {
    if (!id) return;
    const pollEpisodeId = id;
    const hasProcessing = shots.some(s => (
      s.episodeId === pollEpisodeId && s.languageRewriteStatus === 'processing'
    ));
    if (!hasProcessing) return;
    const timer = setInterval(async () => {
      if (!isActiveEpisodeScope(pollEpisodeId)) return;
      const processingIds = shotsRef.current
        .filter(s => s.episodeId === pollEpisodeId && s.languageRewriteStatus === 'processing')
        .map(s => s.id);
      for (const shotId of processingIds) {
        try {
          const presentationEpoch = shotPresentationEpochRef.current;
          const fresh = await getShot(shotId);
          if (
            !isActiveEpisodeScope(pollEpisodeId)
            || shotPresentationEpochRef.current !== presentationEpoch
            || fresh.episodeId !== pollEpisodeId
          ) continue;
          if (fresh.languageRewriteStatus !== 'processing') {
            setShots(prev => prev.map(s => s.id === shotId ? { ...s, ...fresh } : s));
            if (fresh.languageRewriteStatus === 'applied') {
              invalidateDirectorPresentation(pollEpisodeId);
            }
            if (fresh.languageRewriteStatus === 'failed') {
              toast.error(t('toast.langFailed', { error: fresh.languageRewriteError || '' }));
            }
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn('[language-rewrite] poll failed for', shotId, err);
        }
      }
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, shots.map(s => `${s.episodeId}:${s.id}:${s.languageRewriteStatus}`).join('|'), invalidateDirectorPresentation]);

  // 轮询资产匹配状态
  useEffect(() => {
    if (!assetMatchPolling || !id) return;
    const interval = setInterval(() => {
      loadEpisode(id);
      loadShots(id);
    }, 3000);
    return () => clearInterval(interval);
  }, [assetMatchPolling, id, loadEpisode, loadShots]);

  // 轮询所有有 generating 版本的分镜
  useEffect(() => {
    const generatingShotIds = Object.entries(shotVersions)
      .filter(([, versions]) => versions.some(v => v.taskStatus === 'generating'))
      .map(([shotId]) => shotId);
    if (generatingShotIds.length === 0) return;
    const interval = setInterval(() => {
      generatingShotIds.forEach(shotId => loadVersions(shotId));
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shotVersions]);

  // 把 shot id 列表拉平成稳定字符串作为 effect 依赖，避免每次 shots 引用变化（状态轮询）都重订阅 scroll 监听
  const shotIdsKey = shots.map(s => s.id).join(',');

  // 左侧导航：scroll-spy 追踪当前可视的分镜
  // 参考 ProjectDetail 资产分析的实现（rAF + 阈值线距离），对大高度卡片更稳健
  useEffect(() => {
    const shotIds = shotIdsKey ? shotIdsKey.split(',') : [];
    // shots 变化时清空上一组的 primary/weights 缓存，避免把旧剧集的状态带进新一组
    prevPrimaryRef.current = null;
    prevWeightsRef.current = {};
    if (shotIds.length === 0) {
      setActiveShotId(null);
      setShotWeights({});
      return;
    }

    let rafId = 0;
    const onScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const headerH = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 58;
        const threshold = headerH + 20;
        const rects: ShotRect[] = [];
        for (const shotId of shotIds) {
          const el = document.getElementById('shot-' + shotId);
          if (!el) continue;
          const rect = el.getBoundingClientRect();
          rects.push({ id: shotId, top: rect.top, bottom: rect.bottom });
        }
        const { weights, primaryId } = computeShotHighlights(
          rects, threshold, window.innerHeight, prevPrimaryRef.current,
        );
        // 变更守卫：量化后权重无变化则不 setState，避免逐帧重渲染
        if (!shallowEqualWeights(prevWeightsRef.current, weights)) {
          prevWeightsRef.current = weights;
          setShotWeights(weights);
        }
        if (primaryId !== prevPrimaryRef.current) {
          prevPrimaryRef.current = primaryId;
          setActiveShotId(primaryId);
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // 初始化
    return () => { window.removeEventListener('scroll', onScroll); cancelAnimationFrame(rafId); };
  }, [shotIdsKey]);

  // 当前分镜变化时，确保侧栏里对应项保持可见
  useEffect(() => {
    if (!activeShotId) return;
    const el = document.getElementById('nav-shot-' + activeShotId);
    if (!el) return;
    const y = window.scrollY;
    el.scrollIntoView({ block: 'nearest' });
    if (window.scrollY !== y) window.scrollTo(0, y);
  }, [activeShotId]);

  // 从外部（如工单详情）带 #shot-<id> 进入时，分镜列表就绪后滚动到对应分镜。
  // 因为分镜卡片内含异步加载的图片，layout 会抖动，所以分两阶段滚：
  // 立即滚一次定位，再在 300ms 后补滚一次校准，覆盖图片加载带来的高度变化。
  const hashScrollHandledRef = useRef<string | null>(null);
  useEffect(() => {
    const hash = location.hash;
    if (!hash.startsWith('#shot-')) return;
    if (hashScrollHandledRef.current === hash) return;
    if (!shotIdsKey) return;
    const targetId = hash.slice('#shot-'.length);
    if (!shotIdsKey.split(',').includes(targetId)) return;
    hashScrollHandledRef.current = hash;
    const scrollToShot = (): void => {
      const el = document.getElementById('shot-' + targetId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    const raf = requestAnimationFrame(scrollToShot);
    const timer = window.setTimeout(scrollToShot, 300);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [location.hash, shotIdsKey]);

  // 进入分镜页时恢复"上次所在的分镜"：仅整页首次进入触发（覆盖从资产页点 toggle 进入的场景），
  // 不影响站内换集时的置顶行为；显式 #shot 深链优先。
  const initialShotRestoreRef = useRef(false);
  useEffect(() => {
    if (initialShotRestoreRef.current) return;
    if (!shotIdsKey || !episode?.id) return; // 等分镜列表就绪
    initialShotRestoreRef.current = true;
    if (location.hash.startsWith('#shot-')) return;
    const saved = localStorage.getItem(`lastShot:${episode.id}`);
    if (!saved || !shotIdsKey.split(',').includes(saved)) return;
    const scrollToShot = () => {
      const el = document.getElementById('shot-' + saved);
      if (el) el.scrollIntoView({ block: 'start' });
    };
    const raf = requestAnimationFrame(scrollToShot);
    const timer = window.setTimeout(scrollToShot, 300);
    return () => { cancelAnimationFrame(raf); window.clearTimeout(timer); };
  }, [shotIdsKey, episode?.id, location.hash]);

  // 记录当前所在分镜（恢复完成后才开始写，避免开局 scroll-spy 置顶覆盖掉上次记录）
  useEffect(() => {
    if (!initialShotRestoreRef.current) return;
    if (activeShotId && episode?.id) localStorage.setItem(`lastShot:${episode.id}`, activeShotId);
  }, [activeShotId, episode?.id]);

  const handleGeneratePrompts = async () => {
    if (!id) return;
    if (shots.length > 0) {
      setConfirmRegenerate(true);
      return;
    }
    await doGeneratePrompts();
  };

  const doGeneratePrompts = async () => {
    if (!id) return;
    const requestEpisodeId = activeEpisodeIdRef.current;
    setConfirmRegenerate(false);
    setGeneratingPrompts(true);
    try {
      await generatePrompts(requestEpisodeId);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      setShots([]);
      invalidateDirectorPresentation(requestEpisodeId);
      promptGenForcePollRef.current = true;
      setPolling(true);
      await loadEpisode(requestEpisodeId);
    } catch (err: any) {
      toast.error(err.message || t('toast.generatePromptsFailed'));
    } finally {
      setGeneratingPrompts(false);
    }
  };

  const handleSaveContent = async () => {
    if (!id) return;
    setSavingContent(true);
    try {
      await updateEpisode(id, editedContent);
      setContent(editedContent);
      setOriginalContent(editedContent);
      setEditingContent(false);
      toast.success(t('content.scriptSaved'));
      setShowNormalizePrompt(true);
    } catch (err) {
      console.error('Failed to save content:', err);
      toast.error(t('content.saveFailed'));
    } finally {
      setSavingContent(false);
    }
  };

  const handleEditShot = (shot: Shot) => {
    setEditingShot(shot.id);
    setShotEdits({ duration: shot.duration, description: shot.description, remark: shot.remark, prompt: shot.prompt });
  };

  const handleSaveShot = async (shotId: string): Promise<boolean> => {
    if (videoLimits.resolved) {
      const { minDurationSec, maxDurationSec } = videoLimits.limits;
      if (shotEdits.duration < minDurationSec) {
        toast.error(t('shot.durationTooShort', { limit: minDurationSec, actual: shotEdits.duration }));
        return false;
      }
      if (shotEdits.duration > maxDurationSec) {
        toast.error(t('shot.durationTooLong', { limit: maxDurationSec, actual: shotEdits.duration }));
        return false;
      }
    }
    const previous = getActiveOwnedShot(shotId);
    if (!previous) return false;
    const requestEpisodeId = previous.episodeId;
    setSavingShot(true);
    try {
      await updateShot(shotId, shotEdits);
      if (!isActiveEpisodeScope(requestEpisodeId)) return false;
      setEditingShot(null);
      if (!previous || (
        previous.duration !== shotEdits.duration
        || previous.description !== shotEdits.description
        || previous.prompt !== shotEdits.prompt
      )) {
        invalidateDirectorPresentation(requestEpisodeId);
      }
      loadShots(requestEpisodeId);
      return true;
    } catch (err) {
      console.error('Failed to save shot:', err);
      toast.error(t('content.saveFailed'));
      return false;
    } finally {
      setSavingShot(false);
    }
  };

  const handleSaveAndGenerate = (shot: Shot) =>
    saveThenGenerate({
      save: () => handleSaveShot(shot.id),
      generate: () => handleGenerateVideoClick(shot),
    });

  const handleInsertShot = async () => {
    if (insertAfterIndex === null || !id) return;
    if (videoLimits.resolved) {
      const { minDurationSec, maxDurationSec } = videoLimits.limits;
      if (insertShotData.duration < minDurationSec) {
        toast.error(t('shot.durationTooShort', { limit: minDurationSec, actual: insertShotData.duration }));
        return;
      }
      if (insertShotData.duration > maxDurationSec) {
        toast.error(t('shot.durationTooLong', { limit: maxDurationSec, actual: insertShotData.duration }));
        return;
      }
    }
    const requestEpisodeId = activeEpisodeIdRef.current;
    setInsertingShotLoading(true);
    try {
      await createShot(requestEpisodeId, insertAfterIndex, {
        duration: insertShotData.duration || 5,
        prompt: insertShotData.prompt,
      });
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      invalidateDirectorPresentation(requestEpisodeId);
      setInsertAfterIndex(null);
      setInsertShotData({ duration: 5, prompt: '' });
      loadShots(requestEpisodeId);
      toast.success(t('shot.shotInserted'));
    } catch (err) {
      console.error('Failed to insert shot:', err);
      toast.error(t('shot.insertFailed'));
    } finally {
      setInsertingShotLoading(false);
    }
  };

  const handleDeleteShot = async () => {
    if (!deleteShotTarget || !id) return;
    const target = deleteShotTarget;
    const ownedShot = getActiveOwnedShot(target.id);
    if (!ownedShot) return;
    const requestEpisodeId = ownedShot.episodeId;
    setDeletingShotLoading(true);
    try {
      await deleteShot(target.id);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      invalidateDirectorPresentation(requestEpisodeId);
      setDeleteShotTarget(null);
      setDeleteShotStep(1);
      loadShots(requestEpisodeId);
      toast.success(t('shot.shotDeleted', { index: target.index }));
    } catch (err: any) {
      toast.error(err.message || t('shot.deleteFailed'));
    } finally {
      setDeletingShotLoading(false);
    }
  };

  const handleDuplicateShot = async () => {
    if (!duplicateShotTarget || !id) return;
    const target = duplicateShotTarget;
    const ownedShot = getActiveOwnedShot(target.id);
    if (!ownedShot || target.episodeId !== ownedShot.episodeId) return;
    const requestEpisodeId = ownedShot.episodeId;
    setDuplicatingShotLoading(true);
    try {
      const newShot = await duplicateShot(target.id);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      invalidateDirectorPresentation(requestEpisodeId);
      setDuplicateShotTarget(null);
      loadShots(requestEpisodeId);
      toast.success(t('shot.shotDuplicated', { index: newShot.index }));
    } catch (err: any) {
      toast.error(err.message || t('shot.duplicateFailed'));
    } finally {
      setDuplicatingShotLoading(false);
    }
  };

  const clearPendingVideoForShot = (shotId: string) => {
    setPendingVideoSubmissions(prev => clearPendingVideoSubmission(prev, shotId));
  };

  const isCurrentVideoSubmission = (ctx: VideoSubmitContext) => (
    videoSubmissionScopeRef.current === ctx.submissionScope &&
    pendingVideoSubmissionTokensRef.current[ctx.shotId] === ctx.submissionToken
  );

  const refreshShotsAfterVideoSubmit = async (episodeId: string, ctx: VideoSubmitContext) => {
    const presentationEpoch = shotPresentationEpochRef.current;
    const requestId = ++shotLoadRequestRef.current;
    try {
      const data = await listShots(episodeId);
      if (
        !isCurrentVideoSubmission(ctx)
        || activeEpisodeIdRef.current !== episodeId
        || shotPresentationEpochRef.current !== presentationEpoch
        || shotLoadRequestRef.current !== requestId
      ) return;

      setShots(data);
      const saved: Record<string, string> = {};
      const savedSecondary: Record<string, string[]> = {};
      data.forEach(shot => {
        if (shot.selectedVersionId) saved[shot.id] = shot.selectedVersionId;
        if (shot.secondaryVersionIds) {
          try {
            const ids = JSON.parse(shot.secondaryVersionIds);
            if (Array.isArray(ids)) savedSecondary[shot.id] = ids;
          } catch { /* ignore */ }
        }
      });
      setSelectedVideoVersions(prev => ({ ...saved, ...prev }));
      setSecondaryVersions(prev => ({ ...savedSecondary, ...prev }));
    } catch (err) {
      console.error('Failed to refresh shots after submitted video:', err);
    }
  };

  const handleGenerateVideo = async (shotId: string) => {
    if (pendingVideoSubmissionTokensRef.current[shotId]) return;
    const target = getActiveOwnedShot(shotId);
    if (!target) return;

    const submissionToken = Symbol(shotId);
    const submissionScope = videoSubmissionScopeRef.current;
    const ctx = { shotId, submissionToken, submissionScope };
    pendingVideoSubmissionTokensRef.current[shotId] = submissionToken;

    await runVideoSubmitFlow(ctx, {
      episodeId: target.episodeId,
      isCurrent: isCurrentVideoSubmission,
      markPending: (pendingShotId, value) => {
        setPendingVideoSubmissions(prev => markPendingVideoSubmission(prev, pendingShotId, value));
      },
      clearPending: (pendingShotId) => {
        if (!isCurrentVideoSubmission(ctx)) return;
        delete pendingVideoSubmissionTokensRef.current[shotId];
        clearPendingVideoForShot(pendingShotId);
      },
      generateVideo,
      refreshShots: refreshShotsAfterVideoSubmit,
      listShotVersions,
      setShotVersions: (versionsShotId, versions) => {
        setShotVersions(prev => ({ ...prev, [versionsShotId]: versions }));
      },
      info: () => toast.info(t('toast.videoTaskSubmittedHistoryPending')),
      error: (err: any) => toast.error(err.message || t('toast.generateVideoFailed')),
      log: (message, err) => console.error(message, err),
    });
  };

  const handleApplyLanguage = async (shotId: string) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    setApplyingLanguage(shotId);
    try {
      await applyLanguageToShot(shotId);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      // Optimistic: mark shot as processing so the button flips immediately.
      setShots(prev => prev.map(s => s.id === shotId
        ? { ...s, languageRewriteStatus: 'processing', languageRewriteError: undefined }
        : s));
    } catch (err: any) {
      toast.error(err?.message || t('toast.langProcessFailed'));
    } finally {
      setApplyingLanguage(null);
    }
  };

  const handleGenerateVideoWithLangCheck = (shot: Shot) => {
    const needsWarn =
      !!projectData?.languageSkillId &&
      shot.languageRewriteStatus !== 'applied';
    if (needsWarn) {
      setWarnShot(shot);
      return;
    }
    handleGenerateVideo(shot.id);
  };

  const handleCancelVideo = async (shotId: string, versionId: string, externalTaskId: string) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    setCancelingVideo(versionId);
    try {
      await cancelVideo(shotId, versionId);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      loadShots(requestEpisodeId);
      loadVersions(shotId);
    } catch (err: any) {
      // 取消失败：标记该 task 不再显示取消按钮
      setCancelFailedTaskIds(prev => new Set(prev).add(externalTaskId));
      toast.error(t('toast.cancelVideoFailed'));
    } finally {
      setCancelingVideo(null);
    }
  };

  const handleRematchAssets = async (shotId: string) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    setRematchingAssets(shotId);
    try {
      const result = await rematchShotAssets(shotId);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      setShotAssetRefs(prev => ({ ...prev, [shotId]: result.assetRefs }));
      setShots(prev => prev.map(s => s.id === shotId
        ? { ...s, assetRefs: JSON.stringify(result.assetRefs), prompt: result.prompt, assetRefsManual: false }
        : s
      ));
      invalidateDirectorPresentation(requestEpisodeId);
      // 用户可能已在资产设定中更换了候选图，刷新 assetLookup 让卡片显示最新状态图，
      // 同步刷新审核状态表（按 live URL 查询，换图后旧表会误判未审核）。
      // 刷新失败不影响匹配结果本身，静默忽略
      if (episode?.projectId) {
        refreshAnalysisAssets(episode.projectId).catch(() => {});
      }
      loadAssetReviewStatus();
    } catch (err: any) {
      toast.error(err.message || t('toast.matchResourceFailed'));
    } finally {
      setRematchingAssets(null);
    }
  };

  const handleRematchEpisodeAssets = async () => {
    if (!id) return;
    const requestEpisodeId = activeEpisodeIdRef.current;
    setRematchingEpisodeAssets(true);
    localStorage.setItem(`assetMatch_${requestEpisodeId}`, Date.now().toString());
    try {
      await matchEpisodeAssetStates(requestEpisodeId);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      invalidateDirectorPresentation(requestEpisodeId);
      // 后端返回 202，异步执行中，开始轮询
      setAssetMatchPolling(true);
      toast.success(t('toast.assetMatchStarted'));
    } catch (err: any) {
      setRematchingEpisodeAssets(false);
      localStorage.removeItem(`assetMatch_${requestEpisodeId}`);
      if (isActiveEpisodeScope(requestEpisodeId)) {
        toast.error(err.message || t('toast.assetMatchFailed'));
      }
    }
  };

  // 检查单个分镜的人物资产是否全部审核通过
  // 与后端 handler_shot.go GenerateVideo 的逻辑对齐：
  // 以分析数据中 name+state 对应的 selectedImageURL（live URL）作为审核状态查询键，
  // 而非 shot.assetRefs 中可能过期的快照 URL（ref.imageUrl）。
  const checkShotReviewPassed = useCallback((shot: Shot): { passed: boolean; unreviewedNames: string[] } => {
    if (!assetReviewRequired) return { passed: true, unreviewedNames: [] };

    // 获取该 shot 的 assetRefs
    const refs: AssetRef[] = shotAssetRefs[shot.id] ?? parseAssetRefs(shot.assetRefs);
    if (refs.length === 0) return { passed: true, unreviewedNames: [] };

    // 人物名集合（小写）
    const charNameSet = new Set(analysisAssets.characters.map(c => c.name.toLowerCase()));

    // 分析数据 name|state → live imageUrl（与后端 assetImageLookup 对齐）
    const liveUrlByNameState = new Map<string, string>();
    for (const c of analysisAssets.characters) {
      for (const s of c.allStates) {
        if (s.imageUrl) {
          liveUrlByNameState.set(`${c.name.toLowerCase()}|${(s.state || '').toLowerCase()}`, s.imageUrl);
        }
      }
    }

    // 审核状态查找表：imageUrl(live) → status
    const reviewStatusMap = new Map(assetReviews.map(r => [r.imageUrl, r.status]));

    const unreviewedNames: string[] = [];
    for (const ref of refs) {
      if (!charNameSet.has(ref.name.toLowerCase())) continue; // 非人物资产，跳过
      // 先用 live URL（与后端 fileURLs 构建逻辑一致），回退到快照 URL
      const liveUrl = liveUrlByNameState.get(`${ref.name.toLowerCase()}|${(ref.state || '').toLowerCase()}`) || ref.imageUrl;
      if (!liveUrl) continue;
      const status = reviewStatusMap.get(liveUrl);
      if (status !== 'active') {
        unreviewedNames.push(ref.name);
      }
    }
    return { passed: unreviewedNames.length === 0, unreviewedNames };
  }, [assetReviewRequired, shotAssetRefs, analysisAssets.characters, assetReviews]);

  // 加载人物审核状态
  const prevReviewsRef = useRef<AssetReviewItem[]>([]);
  const loadAssetReviewStatus = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getAssetReviewStatus(id);
      setAssetReviewRequired(res.required);
      setAssetReviews(res.reviews || []);
      setAssetReviewAllActive(res.allActive);

      // 对比前后状态，发出 toast 通知
      const prev = prevReviewsRef.current;
      if (prev.length > 0 && res.reviews) {
        const prevMap = new Map(prev.map(r => [r.imageUrl, r.status]));
        for (const r of res.reviews) {
          const oldStatus = prevMap.get(r.imageUrl);
          if (oldStatus && oldStatus !== r.status) {
            if (r.status === 'active') {
              toast.success(t('review.passed', { name: r.characterName }));
            } else if (r.status === 'failed') {
              toast.error(r.errorMsg ? t('review.failedWithMsg', { name: r.characterName, msg: r.errorMsg }) : t('review.failed', { name: r.characterName }));
            }
          }
        }
        if (!prevReviewsRef.current.every(r => r.status === 'active') && res.allActive) {
          toast.success(t('review.allPassedToast'));
        }
      }
      prevReviewsRef.current = res.reviews || [];
    } catch (err) {
      // silently ignore polling errors
    }
  }, [id, t, toast]);

  // 提交人物审核
  const handleSubmitAssetReview = async () => {
    if (!id) return;
    setSubmittingReview(true);
    try {
      const res = await submitAssetReview(id);
      const submitToast = getAssetReviewSubmitToast(t, res);
      if (submitToast.level === 'error') {
        toast.error(submitToast.message);
      } else {
        toast.success(submitToast.message);
      }
      // 开始轮询审核状态
      loadAssetReviewStatus();
    } catch (err: any) {
      toast.error(err.message || t('review.submitFailed'));
    } finally {
      setSubmittingReview(false);
    }
  };

  // 审核状态轮询：状态从非 processing 变为 processing 后也必须启动轮询。
  const hasProcessingAssetReview = assetReviewRequired && !assetReviewAllActive && assetReviews.some(r => r.status === 'processing');
  useProcessingPoll(hasProcessingAssetReview, loadAssetReviewStatus, 5000);

  // 初始加载审核状态
  useEffect(() => {
    if (id && projectData) {
      loadAssetReviewStatus();
    }
  }, [id, projectData, loadAssetReviewStatus]);

  // 资源审核状态加载
  const loadResourceReviewStatus = useCallback(async () => {
    if (!id) return;
    try {
      const res = await getResourceReviewStatus(id);
      setResourceReviews(res.reviews || []);
      setResourceReviewRequired(res.required);
      setResourceReviewAllActive(res.allActive);
    } catch {
      // silently ignore
    }
  }, [id]);

  useEffect(() => {
    if (id && projectData?.projectType === 'real_person' && platformSupportsResourceReview(projectData?.videoPlatform)) {
      loadResourceReviewStatus();
    }
  }, [id, projectData, loadResourceReviewStatus]);

  // 资源审核轮询：提交后资源状态变为 processing 时立即启动轮询，终态后停止。
  const hasProcessingResourceReview = resourceReviews.some(r => r.status === 'processing');
  useProcessingPoll(hasProcessingResourceReview, loadResourceReviewStatus, 5000);

  // 打开选择资源弹窗时加载项目级已审核素材（跨剧集引用）
  useEffect(() => {
    if (!pickingAssetFor || !projectData || projectData.projectType !== 'real_person' || !platformSupportsResourceReview(projectData.videoPlatform)) return;
    getProjectResourceReviews(projectData.id, 'active').then(res => {
      setProjectActiveReviews(res.reviews || []);
    }).catch(() => {});
  }, [pickingAssetFor, projectData]);

  // 项目级素材库加载（「其他素材」tab，不受 projectType/平台限制）
  const loadProjectAssets = useCallback(() => {
    if (!projectData) return;
    getProjectAssets(projectData.id)
      .then(res => setProjectAssets(res.assets || []))
      .catch(() => {});
  }, [projectData]);

  // 打开选择资源弹窗时刷新素材库
  useEffect(() => {
    if (!pickingAssetFor) return;
    loadProjectAssets();
  }, [pickingAssetFor, loadProjectAssets]);

  // 合并审核面板：人物审核 + 素材审核全部通过时整体自动折叠，任一回退时自动展开
  // （仅在状态变化时触发，不覆盖用户手动操作）
  useEffect(() => {
    const showAsset = assetReviewRequired && assetReviews.length > 0;
    const showResource = resourceReviews.length > 0;
    const panelVisible = showAsset || showResource;
    if (!panelVisible) {
      // 面板不可见时重置状态，避免下次出现时残留折叠
      prevAllActiveRef.current = null;
      setReviewPanelCollapsed(false);
      return;
    }
    const combinedAllActive =
      (!showAsset || assetReviewAllActive) &&
      (!showResource || resourceReviews.every(r => r.status === 'active'));
    if (prevAllActiveRef.current !== null && prevAllActiveRef.current !== combinedAllActive) {
      setReviewPanelCollapsed(combinedAllActive);
    }
    if (prevAllActiveRef.current === null && combinedAllActive) {
      setReviewPanelCollapsed(true);
    }
    prevAllActiveRef.current = combinedAllActive;
  }, [assetReviewAllActive, assetReviewRequired, assetReviews.length, resourceReviews]);

  const loadVersions = async (shotId: string) => {
    try {
      const versions = await listShotVersions(shotId);
      setShotVersions((prev) => ({ ...prev, [shotId]: versions }));
    } catch (err) {
      console.error('Failed to load versions:', err);
    }
  };

  const handleExpandShot = async (shotId: string) => {
    if (!getActiveOwnedShot(shotId)) return;
    const isExpanded = !collapsedShots.has(shotId);
    setCollapsedShots(prev => {
      const next = new Set(prev);
      if (isExpanded) {
        next.add(shotId);
      } else {
        next.delete(shotId);
      }
      return next;
    });
    if (isExpanded) return;
    if (!shotResources[shotId]) {
      try {
        const resources = await listResources(shotId);
        setShotResources((prev) => ({ ...prev, [shotId]: resources }));
      } catch (err) {
        console.error('Failed to load resources:', err);
      }
    }
    loadVersions(shotId);
  };

  const handleUploadResource = async (shotId: string, file: File) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    let type = 'image';
    if (file.type.startsWith('audio/')) type = 'audio';
    else if (file.type.startsWith('video/')) type = 'video';

    // 文件大小校验
    const sizeMB = file.size / (1024 * 1024);
    if (type === 'image' && sizeMB > 30) { toast.error(t('upload.imageSizeLimit', { size: sizeMB.toFixed(1) })); return; }
    if (type === 'video' && sizeMB > 50) { toast.error(t('upload.videoSizeLimit', { size: sizeMB.toFixed(1) })); return; }
    if (type === 'audio' && sizeMB > 15) { toast.error(t('upload.audioSizeLimit', { size: sizeMB.toFixed(1) })); return; }

    // 图片像素尺寸校验：任一边须在 400-5000px 之间
    if (type === 'image') {
      let dimensions;
      try {
        dimensions = await readImageFileDimensions(file);
      } catch {
        toast.error(t('upload.imageReadFailed'));
        return;
      }
      const dimensionError = validateImageDimensions(dimensions);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      if (dimensionError) {
        toast.error(t('upload.imageDimensionInvalid', { ...dimensionError }));
        return;
      }
      const aspectError = validateImageAspectRatio(dimensions);
      if (aspectError) {
        toast.error(t('imageAspectRatioInvalid', aspectRatioToastParams(aspectError)));
        return;
      }
    }

    // 音频/视频时长校验：最长 15 秒
    if (type === 'audio' || type === 'video') {
      try {
        const duration = await new Promise<number>((resolve, reject) => {
          const el = document.createElement(type === 'video' ? 'video' : 'audio');
          el.preload = 'metadata';
          el.onloadedmetadata = () => { resolve(el.duration); URL.revokeObjectURL(el.src); };
          el.onerror = () => { URL.revokeObjectURL(el.src); reject(new Error(t('upload.cannotReadDuration'))); };
          el.src = URL.createObjectURL(file);
        });
        if (!isActiveEpisodeScope(requestEpisodeId)) return;
        if (duration > 15) {
          toast.error(t(type === 'video' ? 'upload.videoDurationLimit' : 'upload.audioDurationLimit', { duration: Math.round(duration) }));
          return;
        }
      } catch {
        toast.error(t('upload.cannotReadDuration'));
        return;
      }
    }

    // 数量校验：判 current + 1，语义是"再加这一个会不会超"
    if (blockedByRefLimits(shotId, [type as MediaKind])) return;

    setUploadingResource(shotId);
    const hasHuman = pendingContainsHumanRef.current;
    const resName = pendingResourceNameRef.current;
    try {
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      const options = hasHuman
        ? { containsHuman: true, resourceName: resName }
        : undefined;
      const uploadRes = await uploadResource(shotId, type, file, options);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      writeShotPrompt(requestEpisodeId, shotId, uploadRes.prompt);
      const resources = await listResources(shotId);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      setShotResources((prev) => ({ ...prev, [shotId]: resources }));
      if (hasHuman) {
        // 真人项目：上传的人物素材自动提交审核，省去用户手动回审核状态面板点"提交审核"。
        // 仅 toast 非阻断告知（通过后方可生成视频）；自动送审失败则回退提示，仍可在面板手动提交。
        try {
          const res = await submitResourceReview(requestEpisodeId);
          if (!isActiveEpisodeScope(requestEpisodeId)) return;
          if (res.errors?.length) {
            toast.error(t('resourceReview.submitPartialError', { count: res.errors.length }));
          } else {
            toast.success(t('upload.uploadedAndReview'));
          }
        } catch {
          toast.warning(t('upload.uploadedNeedManualSubmit'));
        }
        if (isActiveEpisodeScope(requestEpisodeId)) loadResourceReviewStatus();
      }
    } catch (err: any) {
      toast.error(err.message || t('upload.uploadFailed'));
    } finally {
      setUploadingResource(null);
      setPendingContainsHuman(false);
      pendingContainsHumanRef.current = false;
      setPendingResourceName('');
      pendingResourceNameRef.current = '';
    }
  };

  // 真人剧上传按钮点击处理：弹对话框或直接弹文件选择
  const uploadFileInputRef = useRef<HTMLInputElement | null>(null);
  const handleUploadButtonClick = (shotId: string) => {
    setPendingUploadShotId(shotId);
    pendingUploadShotIdRef.current = shotId;
    if (projectData?.projectType === 'real_person' && platformSupportsResourceReview(projectData?.videoPlatform)) {
      setHumanContentDialogOpen(true);
    } else {
      setPendingContainsHuman(false);
      pendingContainsHumanRef.current = false;
      setTimeout(() => uploadFileInputRef.current?.click(), 0);
    }
  };

  const handleHumanDialogNoHuman = () => {
    setHumanContentDialogOpen(false);
    setPendingContainsHuman(false);
    pendingContainsHumanRef.current = false;
    setTimeout(() => uploadFileInputRef.current?.click(), 0);
  };

  const handleHumanDialogHasHuman = (resourceName: string) => {
    setHumanContentDialogOpen(false);
    setPendingContainsHuman(true);
    pendingContainsHumanRef.current = true;
    setPendingResourceName(resourceName);
    pendingResourceNameRef.current = resourceName;
    setTimeout(() => uploadFileInputRef.current?.click(), 0);
  };

  const handleHumanDialogCancel = () => {
    setHumanContentDialogOpen(false);
    setPendingUploadShotId(null);
    pendingUploadShotIdRef.current = null;
    setPendingContainsHuman(false);
    pendingContainsHumanRef.current = false;
    setPendingResourceName('');
    pendingResourceNameRef.current = '';
  };

  // 资源审核提交
  const handleSubmitResourceReview = useCallback(async () => {
    if (!id) return;
    setSubmittingResourceReview(true);
    try {
      const res = await submitResourceReview(id);
      if (res.errors?.length) {
        toast.error(t('resourceReview.submitPartialError', { count: res.errors.length }));
      }
      await loadResourceReviewStatus();
    } catch (e) {
      toast.error(t('resourceReview.submitFailed'));
    } finally {
      setSubmittingResourceReview(false);
    }
  }, [id, t, toast, loadResourceReviewStatus]);

  // 资源/ref 增删后，后端返回重排后的 prompt（@图片N/@视频N/@音频N 已服务端重编号），写回 shot 状态保持编辑器/展示同步
  const writeShotPrompt = (episodeId: string, shotId: string, prompt?: string, invalidateMapping = true): boolean => {
    const target = getActiveOwnedShot(shotId);
    if (prompt == null || !target || target.episodeId !== episodeId || !isActiveEpisodeScope(episodeId)) return false;
    setShots(prev => prev.map(s => s.id === shotId && s.episodeId === episodeId ? { ...s, prompt } : s));
    if (invalidateMapping) invalidateDirectorPresentation(episodeId);
    return true;
  };

  const handleRemoveAssetRef = async (shotId: string, imgIndex: number) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    try {
      const { assetRefs, prompt } = await removeShotAssetRef(shotId, imgIndex);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      setShotAssetRefs((prev) => ({ ...prev, [shotId]: assetRefs }));
      setShots((prev) => prev.map((s) => s.id === shotId ? { ...s, assetRefs: JSON.stringify(assetRefs), assetRefsManual: true } : s));
      writeShotPrompt(requestEpisodeId, shotId, prompt);
    } catch (err) {
      console.error('Failed to remove asset ref:', err);
    }
  };

  // 选择资源弹窗的统一关闭清理：四条关闭路径（onClose 与三个添加通路的 finally）口径一致，
  // 防止勾选/搜索残留到下一个分镜误加
  const closeAssetPicker = () => {
    setPickingAssetFor(null);
    setPickerSelected([]);
    setPickerSearch('');
    setLibrarySelectedIds([]);
  };

  const handleAddAssetRefs = async (shotId: string, assets: AnalysisAssetItem[]) => {
    if (assets.length === 0) return;
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    // 资产选择器是参考图最主要的挂载通路。批内分类见 assetPickerAdditionKinds。
    if (blockedByRefLimits(shotId, assetPickerAdditionKinds(assets, projectActiveReviews))) return;
    setAddingAssets(true);
    try {
      // 区分已审核资源引用 vs 普通资产引用
      const reviewedAssets = assets.filter(a => a.state === '__reviewed__');
      const normalAssets = assets.filter(a => a.state !== '__reviewed__');
      let invalidated = false;

      // 已审核素材：通过 createRefResource 创建资源引用
      for (const asset of reviewedAssets) {
        const review = findActiveReviewForAsset(asset, projectActiveReviews);
        if (!review) continue;
        const { prompt } = await createRefResource(shotId, review.resourceUrl, review.fileName || review.name, review.resourceType, true);
        if (!isActiveEpisodeScope(requestEpisodeId)) return;
        if (writeShotPrompt(requestEpisodeId, shotId, prompt, !invalidated)) invalidated = true;
      }
      if (reviewedAssets.length > 0) {
        const resources = await listResources(shotId);
        if (!isActiveEpisodeScope(requestEpisodeId)) return;
        setShotResources((prev) => ({ ...prev, [shotId]: resources }));
      }

      // 普通资产：通过 addShotAssetRef 创建引用
      let lastRefs: AssetRef[] | null = null;
      for (const asset of normalAssets) {
        const result = await addShotAssetRef(shotId, asset.type, asset.name, asset.state, asset.imageUrl, asset.allStates);
        if (!isActiveEpisodeScope(requestEpisodeId)) return;
        lastRefs = result.assetRefs;
        if (writeShotPrompt(requestEpisodeId, shotId, result.prompt, !invalidated)) invalidated = true;
      }
      if (lastRefs) {
        setShotAssetRefs((prev) => ({ ...prev, [shotId]: lastRefs! }));
        setShots((prev) => prev.map((s) => s.id === shotId
          ? { ...s, assetRefs: JSON.stringify(lastRefs!), assetRefsManual: true }
          : s));
      }
    } catch (err) {
      console.error('Failed to add asset ref:', err);
      toast.error(t('pickerModal.addFailed'));
    } finally {
      setAddingAssets(false);
      closeAssetPicker();
    }
  };

  const togglePickerAsset = (asset: AnalysisAssetItem) => {
    const key = `${asset.type}__${asset.name}__${asset.state}`;
    setPickerSelected(prev => {
      const exists = prev.some(a => `${a.type}__${a.name}__${a.state}` === key);
      if (exists) return prev.filter(a => `${a.type}__${a.name}__${a.state}` !== key);
      return [...prev, asset];
    });
  };

  // 「其他素材」独立添加通路：按 ProjectAsset.id 精确匹配（reviewed tab 的 name 匹配有重名隐患）
  const handleAddLibraryAssets = async (shotId: string, assets: ProjectAsset[]) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    // 素材库同样走 createRefResource 建 Resource，按 ProjectAsset.type 归类。
    // ProjectAsset.type 只有 'image' | 'video'，不要写 asset.type === 'audio' 的分支
    // —— 类型窄化后该比较无交集，TS2367 会让 build 直接失败。
    const additionKinds = assets.map((asset): MediaKind => (asset.type === 'video' ? 'video' : 'image'));
    if (blockedByRefLimits(shotId, additionKinds)) return;
    setAddingAssets(true);
    try {
      let invalidated = false;
      for (const asset of assets) {
        const { prompt } = await createRefResource(shotId, asset.url, asset.name || asset.fileName, asset.type, false);
        if (!isActiveEpisodeScope(requestEpisodeId)) return;
        if (writeShotPrompt(requestEpisodeId, shotId, prompt, !invalidated)) invalidated = true;
      }
    } catch (err) {
      console.error('Failed to add library asset:', err);
      toast.error(t('pickerModal.addFailed'));
    } finally {
      try {
        if (isActiveEpisodeScope(requestEpisodeId)) {
          const resources = await listResources(shotId);
          if (isActiveEpisodeScope(requestEpisodeId)) {
            setShotResources((prev) => ({ ...prev, [shotId]: resources }));
          }
        }
      } catch {
        // 刷新失败不再叠加报错
      }
      setAddingAssets(false);
      closeAssetPicker();
    }
  };

  const handleAddVideoRef = async (shotId: string, sourceShotIndex: number, videoURL: string) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    setAddingVideoRef(true);
    try {
      const name = t('shotVideoRefName', { index: String(sourceShotIndex).padStart(2, '0') });
      const result = await addShotAssetRef(shotId, undefined, name, '', videoURL, [], 'shot_ref');
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      setShotAssetRefs((prev) => ({ ...prev, [shotId]: result.assetRefs }));
      setShots((prev) => prev.map((s) => s.id === shotId
        ? { ...s, assetRefs: JSON.stringify(result.assetRefs), assetRefsManual: true }
        : s));
      writeShotPrompt(requestEpisodeId, shotId, result.prompt);
      toast.success(t('videoPickerModal.addedVideoRef', { index: String(sourceShotIndex).padStart(2, '0') }));
    } catch (err: any) {
      toast.error(err.message || t('videoPickerModal.addVideoRefFailed'));
    } finally {
      setAddingVideoRef(false);
      closeAssetPicker();
    }
  };

  const handleSwitchAssetRef = async (shotId: string, imgIndex: number, state: string, imageUrl: string) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    try {
      const result = await updateShotAssetRef(shotId, imgIndex, state, imageUrl);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      setShotAssetRefs((prev) => ({ ...prev, [shotId]: result.assetRefs }));
      setShots((prev) => prev.map((s) => s.id === shotId
        ? { ...s, assetRefs: JSON.stringify(result.assetRefs), assetRefsManual: true }
        : s));
      if (stateSelectorData) {
        setStateSelectorData({ ...stateSelectorData, currentState: state, currentImageUrl: imageUrl });
      }
    } catch (err: any) {
      toast.error(err.message || t('toast.switchStateFailed'));
    }
  };

  // 状态切换弹窗从实时 assetLookup 取该资产状态（刷新后自动反映新状态）；失配回退打开时的快照
  const liveStateSelectorData = useMemo(() => {
    if (!stateSelectorData) return null;
    const found = findAssetLookupEntry(assetLookup, {
      name: stateSelectorData.assetName,
      type: stateSelectorData.assetType ?? undefined,
      state: stateSelectorData.currentState,
      imageUrl: stateSelectorData.currentImageUrl,
    });
    if (!found || found.entry.allStates.length === 0) return stateSelectorData;
    return { ...stateSelectorData, allStates: found.entry.allStates };
  }, [stateSelectorData, assetLookup]);

  const getShotCredits = (shot: Shot): number | null => {
    if (!projectData?.videoPlatform || !projectData?.videoModel) return null;
    const vm = videoModels.find(m => m.platform === projectData.videoPlatform && m.model === projectData.videoModel);
    if (!vm) return null;
    return vm.creditsPerSec * (shot.duration || 0);
  };

  const getAssetRefs = (shot: Shot): AssetRef[] => {
    const raw: AssetRef[] = shotAssetRefs[shot.id] ?? parseAssetRefs(shot.assetRefs);
    if (raw.length === 0 || assetLookup.size === 0) return raw;
    // 用数据源实时解析 imageUrl 和 allStates
    return raw.map(ref => {
      const found = findAssetLookupEntry(assetLookup, ref);
      if (!found) return ref;
      const imageUrl = found.entry.stateMap.get(ref.state || '') ?? ref.imageUrl;
      const allStates = found.entry.allStates.length > 0 ? found.entry.allStates : ref.allStates;
      return { ...ref, type: ref.type || found.type, imageUrl, allStates };
    });
  };

  // 返回 true 表示应当拦下（已 toast）。additionKinds 是本次要新增的素材种类。
  // resolved === false 时放行，后端是唯一权威闸口。
  const blockedByRefLimits = (shotId: string, additionKinds: MediaKind[]): boolean => {
    if (!videoLimits.resolved) return false;
    const shot = shots.find(s => s.id === shotId);
    if (!shot) return false;
    const current = countShotRefs(getAssetRefs(shot), shotResources[shotId] || []);
    const exceeded = firstExceededLimit(videoLimits.limits, addCounts(current, countKinds(additionKinds)));
    if (!exceeded) return false;
    toast.error(t(`upload.${LIMIT_TOAST_SUFFIX[exceeded.kind]}`, { max: exceeded.limit }));
    return true;
  };

  const handleDeleteResource = async (resourceId: string, shotId: string) => {
    const target = getActiveOwnedShot(shotId);
    if (!target) return;
    const requestEpisodeId = target.episodeId;
    try {
      const { prompt } = await deleteResource(resourceId);
      if (!isActiveEpisodeScope(requestEpisodeId)) return;
      const newResources = (shotResources[shotId] || []).filter(r => r.id !== resourceId);
      setShotResources((prev) => ({ ...prev, [shotId]: newResources }));
      writeShotPrompt(requestEpisodeId, shotId, prompt);
    } catch (err) {
      console.error('Failed to delete resource:', err);
    }
  };

  const handleGenerateVideoClick = (shot: Shot) => {
    const target = getActiveOwnedShot(shot.id);
    if (!target || target.episodeId !== shot.episodeId) return;
    const refs = getAssetRefs(shot);
    const missingStateRefs = refs.filter(ref => ref.source !== 'shot_ref' && !ref.imageUrl);
    if (missingStateRefs.length > 0) {
      const names = missingStateRefs.map(ref => ref.name).join('、');
      toast.error(t('shot.missingStateImage', { names }));
      return;
    }

    // 提交预检判 current（"当前是否已经超"），不 +1。
    const resources = shotResources[shot.id] || [];
    if (videoLimits.resolved) {
      const exceeded = firstExceededLimit(videoLimits.limits, countShotRefs(refs, resources));
      if (exceeded) {
        toast.error(t(`shot.${LIMIT_TOAST_SUFFIX[exceeded.kind]}`, { max: exceeded.limit, count: exceeded.actual }));
        return;
      }
    }

    if (assetReviewRequired) {
      const { passed, unreviewedNames } = checkShotReviewPassed(shot);
      if (!passed) {
        toast.error(t('shot.unreviewedAssets', { names: unreviewedNames.join(', ') }));
        return;
      }
    }

    const { unsubmitted: unsubmittedRes, processing: pendingRes, failed: failedRes } =
      getBlockingResourceReviewsForShot(shot.id, resources, resourceReviews);
    if (unsubmittedRes.length > 0) { toast.error(t('shot.resourceUnsubmitted')); return; }
    if (pendingRes.length > 0) { toast.error(t('shot.reviewInProgress')); return; }
    if (failedRes.length > 0) { toast.error(t('shot.reviewFailed')); return; }

    handleGenerateVideoWithLangCheck(shot);
  };

  const previewableShots = useMemo(() => getPreviewableShots(shots, shotVersions), [shots, shotVersions]);

  // 获取某个分镜选中的视频版本 ID
  const getSelectedVersionId = useCallback(
    (shotId: string): string => resolveSelectedVersionId(shotId, shotVersions, selectedVideoVersions),
    [shotVersions, selectedVideoVersions],
  );

  // 获取某个分镜选中的视频 URL
  const getSelectedVideoURL = useCallback(
    (shotId: string): string => resolveSelectedVideoURL(shotId, shotVersions, selectedVideoVersions, subtitleEraseStatus),
    [shotVersions, selectedVideoVersions, subtitleEraseStatus],
  );

  const toggleSecondaryVersion = useCallback((shotId: string, versionId: string, selected: boolean) => {
    setSecondaryVersions(prev => {
      const current = prev[shotId] || [];
      const next = selected
        ? current.filter(id => id !== versionId)
        : [...current, versionId];
      updateShotSecondaryVersions(shotId, next).catch(() => {});
      return { ...prev, [shotId]: next };
    });
  }, []);

  const selectPrimaryVersion = useCallback((shotId: string, versionId: string) => {
    setSelectedVideoVersions(prev => {
      const oldPrimary = prev[shotId];
      if (oldPrimary && oldPrimary !== versionId) {
        setSecondaryVersions(sp => {
          const current = sp[shotId] || [];
          let next = !current.includes(oldPrimary) ? [...current, oldPrimary] : [...current];
          next = next.filter(id => id !== versionId);
          updateShotSecondaryVersions(shotId, next).catch(() => {});
          return { ...sp, [shotId]: next };
        });
      } else {
        setSecondaryVersions(sp => {
          const current = sp[shotId] || [];
          if (current.includes(versionId)) {
            const next = current.filter(id => id !== versionId);
            updateShotSecondaryVersions(shotId, next).catch(() => {});
            return { ...sp, [shotId]: next };
          }
          return sp;
        });
      }
      return { ...prev, [shotId]: versionId };
    });
    updateShotSelectedVersion(shotId, versionId).catch(() => {});
  }, []);

  // 一键下载所有分镜视频
  const handleDownloadAll = useCallback(async () => {
    if (downloadProgress) return;
    const total = previewableShots.length;
    setDownloadProgress({ current: 0, total });
    let successCount = 0;
    for (let i = 0; i < total; i++) {
      const shot = previewableShots[i];
      const url = getSelectedVideoURL(shot.id);
      if (!url) continue;
      setDownloadProgress({ current: i + 1, total });
      try {
        const idx = String(shot.index).padStart(2, '0');
        const filename = t('download.filename', { project: projectName || t('download.fallbackProject'), episode: episode?.index ?? 1, shot: idx });
        const { downloadUrl } = await getDownloadUrl(url, filename);
        window.open(downloadUrl, '_blank');
        successCount++;
        // 间隔 500ms 避免浏览器拦截多窗口
        if (i < total - 1) await new Promise(r => setTimeout(r, 500));
      } catch (dlErr) {
        console.error(`下载分镜 #${shot.index} 失败:`, url, dlErr);
      }
    }
    setDownloadProgress(null);
    if (successCount === total) {
      toast.success(t('download.downloaded', { count: successCount }));
    } else {
      toast.warning(t('download.partialDownload', { success: successCount, total }));
    }
  }, [previewableShots, getSelectedVideoURL, downloadProgress, projectName, episode, t, toast]);

  const showPreview = previewableShots.length >= 2;
  const showVersionActions = true;

  // --- 音色提取 ---
  const clearExtracting = (versionId: string) =>
    setExtractingVersionIds(prev => {
      if (!prev.has(versionId)) return prev;
      const next = new Set(prev);
      next.delete(versionId);
      return next;
    });

  const handleExtractVoices = async (shotId: string, versionId: string) => {
    setExtractionModal({ shotId, versionId });
    setExtractionTask(null);
    setExtractionLoading(true);
    setAppliedVoices(new Set());
    setVoiceError(prev => { const next = { ...prev }; delete next[versionId]; return next; });
    setExtractingVersionIds(prev => new Set(prev).add(versionId));
    try {
      const task = await extractVoices(shotId, versionId);
      setExtractionTask(task);
      if (task.status === 'pending' || task.status === 'processing' || task.status === 'waiting') {
        pollExtractionStatus(task.taskId, versionId);
      } else {
        // 终态（completed/failed）：解除"提取中"占用；completed 额外标记已提取
        if (task.status === 'completed') {
          setExtractedVersionIds(prev => new Set(prev).add(versionId));
        }
        clearExtracting(versionId);
      }
    } catch (e: any) {
      const msg = e.message || t('extractionModal.extractFailed');
      toast.error(msg);
      setVoiceError(prev => ({ ...prev, [versionId]: msg }));
      setExtractionModal(null);
      clearExtracting(versionId);
    } finally {
      setExtractionLoading(false);
    }
  };

  const pollExtractionStatus = (taskId: string, versionId?: string) => {
    if (extractionPollRef.current) clearInterval(extractionPollRef.current);
    const poll = setInterval(async () => {
      try {
        const task = await getVoiceExtraction(taskId);
        setExtractionTask(task);
        if (task.status !== 'pending' && task.status !== 'processing' && task.status !== 'waiting') {
          clearInterval(poll);
          extractionPollRef.current = null;
          if (task.status === 'completed' && versionId) {
            setExtractedVersionIds(prev => new Set(prev).add(versionId));
          }
          if (versionId) clearExtracting(versionId);
        }
      } catch {
        clearInterval(poll);
        extractionPollRef.current = null;
        if (versionId) clearExtracting(versionId);
      }
    }, 3000);
    extractionPollRef.current = poll;
    // 轮询兜底超时：清掉计时器并解除"提取中"占用，避免按钮永久禁用
    setTimeout(() => {
      clearInterval(poll);
      if (extractionPollRef.current === poll) extractionPollRef.current = null;
      if (versionId) clearExtracting(versionId);
    }, 300000);
  };

  // startExtractionResume 刷新后对"提取中"任务做后台恢复轮询：终态时解除占用 / 标记已提取。
  // 不触碰弹窗 extractionTask（弹窗可能正显示别的版本），按 versionId 多路并行。
  const startExtractionResume = (taskId: string, versionId: string) => {
    if (resumeExtractionPollRefs.current[versionId]) return; // 已在轮询，避免重复
    const finish = (poll: ReturnType<typeof setInterval>) => {
      clearInterval(poll);
      if (resumeExtractionPollRefs.current[versionId] === poll) delete resumeExtractionPollRefs.current[versionId];
      clearExtracting(versionId);
    };
    const poll = setInterval(async () => {
      try {
        const task = await getVoiceExtraction(taskId);
        if (task.status !== 'pending' && task.status !== 'processing' && task.status !== 'waiting') {
          if (task.status === 'completed') {
            setExtractedVersionIds(prev => new Set(prev).add(versionId));
          }
          finish(poll);
        }
      } catch {
        finish(poll);
      }
    }, 3000);
    resumeExtractionPollRefs.current[versionId] = poll;
    setTimeout(() => finish(poll), 300000);
  };

  const handleApplyVoice = async (characterName: string, audioUrl: string) => {
    if (!episode) return;
    setApplyingVoice(characterName);
    try {
      await applyCharacterVoice(episode.projectId, characterName, audioUrl, 'extraction',
        JSON.stringify({ shotId: extractionModal?.shotId, versionId: extractionModal?.versionId }));
      setAppliedVoices(prev => new Set(prev).add(characterName));
      toast.success(t('extractionModal.appliedToast', { name: characterName }));
    } catch (e: any) {
      toast.error(e.message || t('extractionModal.applyFailed'));
    } finally {
      setApplyingVoice(null);
    }
  };

  // --- 换音色 ---
  const handleOpenSwap = async (shotId: string, versionId: string) => {
    setSwapModal({ shotId, versionId });
    setSwapTask(null);
    setSwapCharacters([]);
    // 加载有音色的角色列表
    if (episode?.projectId) {
      try {
        const analysis = await getScriptAnalysis(episode.projectId);
        if (analysis?.characters) {
          const chars: AnalysisCharacter[] = typeof analysis.characters === 'string'
            ? JSON.parse(analysis.characters)
            : analysis.characters;
          setAnalysisChars(collapseCharactersWithVoice(chars));
        }
      } catch { /* ignore */ }
    }
  };

  const handleSwapVoice = async () => {
    if (!swapModal || swapCharacters.length === 0) return;
    const { shotId, versionId } = swapModal;
    setSwapLoading(true);
    setVoiceError(prev => { const next = { ...prev }; delete next[versionId]; return next; });
    try {
      const task = await swapVoice(shotId, versionId, swapCharacters);
      setSwapVersionStatus(prev => ({ ...prev, [versionId]: { status: 'pending', taskId: task.taskId } }));
      setSwapModal(null); // 关闭弹窗，不阻塞
      toast.success(t('swapModal.swapSubmitted'));
      startSwapPoll(versionId, task.taskId);
    } catch (e: any) {
      const msg = e.message || t('swapModal.swapFailed');
      toast.error(msg);
      setVoiceError(prev => ({ ...prev, [versionId]: msg }));
    } finally {
      setSwapLoading(false);
    }
  };

  const startSwapPoll = (versionId: string, taskId: string) => {
    if (swapPollRefs.current[versionId]) clearInterval(swapPollRefs.current[versionId]);
    const poll = setInterval(async () => {
      try {
        const task = await getVoiceSwap(taskId);
        if (task.status !== 'pending' && task.status !== 'processing' && task.status !== 'waiting') {
          clearInterval(poll);
          delete swapPollRefs.current[versionId];
          setSwapVersionStatus(prev => ({
            ...prev,
            [versionId]: {
              status: task.status,
              taskId,
              replacedVocalUrl: task.replacedVocalUrl,
              bgmUrl: task.bgmUrl,
              matchedSegments: task.matchedSegments,
              errorMessage: task.errorMessage,
            },
          }));
          if (task.status === 'completed') {
            toast.success(t('swapModal.swapCompleted'));
          } else {
            toast.error(t('swapModal.swapCompletedFailed', { error: task.errorMessage || '' }));
          }
        }
      } catch {
        clearInterval(poll);
        delete swapPollRefs.current[versionId];
      }
    }, 30000);
    swapPollRefs.current[versionId] = poll;
    setTimeout(() => {
      if (swapPollRefs.current[versionId]) {
        clearInterval(swapPollRefs.current[versionId]);
        delete swapPollRefs.current[versionId];
      }
    }, 7200000); // 2 hours
  };

  // ── 字幕擦除 ─────────────────────────────────
  const handleSubtitleErase = async (versionId: string) => {
    try {
      const r = await submitSubtitleErase(versionId);
      setSubtitleEraseStatus(prev => ({ ...prev, [versionId]: r.data }));
      toast.success(t('toast.subtitleEraseSubmitted'));
      startErasePoll(versionId);
    } catch (e: any) {
      toast.error(e.message || t('toast.subtitleEraseFailed'));
    }
  };

  const startErasePoll = (versionId: string) => {
    if (erasePollRefs.current[versionId]) clearInterval(erasePollRefs.current[versionId]);
    const poll = setInterval(async () => {
      try {
        const r = await getSubtitleEraseStatus(versionId);
        if (!r.data) return;
        setSubtitleEraseStatus(prev => ({ ...prev, [versionId]: r.data! }));
        if (r.data.status !== 'running') {
          clearInterval(poll);
          delete erasePollRefs.current[versionId];
          if (r.data.status === 'completed') {
            toast.success(t('toast.subtitleEraseCompleted'));
          } else {
            toast.error(t('toast.subtitleEraseCompletedFailed', { error: r.data.errorMsg || '' }));
          }
        }
      } catch {
        clearInterval(poll);
        delete erasePollRefs.current[versionId];
      }
    }, 30000);
    erasePollRefs.current[versionId] = poll;
    // 2 小时超时
    setTimeout(() => {
      if (erasePollRefs.current[versionId]) {
        clearInterval(erasePollRefs.current[versionId]);
        delete erasePollRefs.current[versionId];
      }
    }, 7200000);
  };

  startSwapPollRef.current = startSwapPoll;
  startErasePollRef.current = startErasePoll;
  startExtractionResumeRef.current = startExtractionResume;

  // 判断版本是否可擦除字幕（volcano-ark 平台 + 已完成 + 23小时内）
  const canEraseSubtitle = (v: ShotVersion): boolean => {
    if (v.videoPlatform !== 'volcano-ark') return false;
    if (!v.videoURL || v.errorMsg) return false;
    if (!v.completedAt) return false;
    const deadline = new Date(v.completedAt).getTime() + 23 * 60 * 60 * 1000;
    return Date.now() < deadline;
  };

  const handleTogglePreview = useCallback(() => {
    if (!previewExpanded) {
      setPreviewMounted(true);
      setPreviewExpanded(true);
    } else {
      videoRefA.current?.pause();
      videoRefB.current?.pause();
      setPreviewExpanded(false);
    }
  }, [previewExpanded]);

  // Esc 关闭预览分段弹窗
  useEffect(() => {
    if (!previewExpanded) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleTogglePreview();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [previewExpanded, handleTogglePreview]);

  const handlePreviewPlay = useCallback(() => {
    setPreviewPlaying(true);
    const shot = previewableShots[previewCurrentIndex];
    if (!shot) return;
    const versionId = getSelectedVersionId(shot.id);
    if (versionId) markVideoPlayed(versionId);
  }, [previewableShots, previewCurrentIndex, getSelectedVersionId, markVideoPlayed]);

  const handlePreviewEnded = useCallback(() => {
    if (previewCurrentIndex < previewableShots.length - 1) {
      setPreviewCurrentIndex(prev => prev + 1);
    } else {
      setPreviewPlaying(false);
    }
  }, [previewCurrentIndex, previewableShots.length]);

  // 首次加载：通过 ref 设置第一个视频的 src（不再通过 JSX src prop，避免 React 在切换 buffer 时清空 src）
  const initialLoadedRef = useRef(false);
  useEffect(() => {
    if (!showPreview || !previewExpanded || initialLoadedRef.current) return;
    const shot = previewableShots[0];
    if (!shot) return;
    const url = getSelectedVideoURL(shot.id);
    if (!url) return;

    // showPreview 刚变 true 时 video 元素可能还未挂载，轮询等待 ref 就绪
    let cancelled = false;
    let retries = 0;
    const init = () => {
      if (cancelled || initialLoadedRef.current) return;
      const player = videoRefA.current;
      if (!player) {
        // ref 还没挂载，重试（最多 20 次 ≈ 330ms）
        if (retries++ < 20) requestAnimationFrame(init);
        return;
      }
      initialLoadedRef.current = true;
      player.src = tosAccelerate(url);
      player.load();
      // 第一个视频可播放后，标记就绪并预加载第二个视频到 B
      const preloadSecond = () => {
        if (cancelled) return;
        setInitialReady(true);
        if (previewableShots.length > 1) {
          const nextShot = previewableShots[1];
          const nextUrl = nextShot ? getSelectedVideoURL(nextShot.id) : '';
          if (nextUrl && videoRefB.current) {
            videoRefB.current.src = tosAccelerate(nextUrl);
            videoRefB.current.load();
            nextPreloadedUrlRef.current = nextUrl;
          }
        }
      };
      if (player.readyState >= 3) {
        preloadSecond();
      } else {
        player.addEventListener('canplay', preloadSecond, { once: true });
        player.addEventListener('error', () => { if (!cancelled) setInitialReady(true); }, { once: true });
        // 超时兜底：10 秒后如果还没 ready，强制解除 loading 状态
        setTimeout(() => { if (!cancelled) setInitialReady(true); }, 10000);
      }
    };
    requestAnimationFrame(init);
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview, previewableShots, previewExpanded]);

  // 切换分镜时：在后台 buffer 加载视频，就绪后切换；切换完成后预加载下一个视频
  const prevIndexRef = useRef(0);
  useEffect(() => {
    if (!showPreview || !previewExpanded) return;
    // 跳过首次（由上面的 effect 处理）和相同 index
    if (previewCurrentIndex === prevIndexRef.current) return;
    prevIndexRef.current = previewCurrentIndex;

    const shot = previewableShots[previewCurrentIndex];
    if (!shot) return;
    const url = getSelectedVideoURL(shot.id);
    if (!url) return;

    const curActive = activeBufferRef.current;
    const front = curActive === 'A' ? videoRefA.current : videoRefB.current;
    const back = curActive === 'A' ? videoRefB.current : videoRefA.current;
    if (!back || !front) return;

    let cancelled = false;

    // 预加载下一个视频到空闲 buffer（切换完成后调用）
    const preloadNext = () => {
      if (cancelled) return;
      const nextIdx = previewCurrentIndex + 1;
      if (nextIdx >= previewableShots.length) return;
      const nextShot = previewableShots[nextIdx];
      if (!nextShot) return;
      const nextUrl = getSelectedVideoURL(nextShot.id);
      if (!nextUrl) return;
      // 切换后 front 变成空闲 buffer，用它来预加载
      front.src = tosAccelerate(nextUrl);
      front.load();
      nextPreloadedUrlRef.current = nextUrl;
    };

    const switchBuffer = () => {
      if (cancelled) return;
      setSwitchLoading(false);
      back.play().then(() => {
        if (cancelled) return;
        front.pause();
        const next = curActive === 'A' ? 'B' : 'A';
        activeBufferRef.current = next;
        setActiveBuffer(next);
        setPreviewPlaying(true);
        // 切换完成，开始预加载下一个视频到空闲的 front buffer
        preloadNext();
      }).catch(() => {});
    };

    // 检查后台 buffer 是否已经预加载了目标视频
    const backReady = nextPreloadedUrlRef.current === url && back.readyState >= 3;
    nextPreloadedUrlRef.current = '';

    if (backReady) {
      // 预加载命中，直接切换
      switchBuffer();
    } else {
      // 需要加载 — 先注册监听再 load，避免 canplay 在 addEventListener 之前触发的竞态
      setSwitchLoading(true);
      const onError = () => { if (!cancelled) setSwitchLoading(false); };
      back.addEventListener('canplay', switchBuffer, { once: true });
      back.addEventListener('error', onError, { once: true });
      back.src = tosAccelerate(url);
      back.load();
      // 同步检查：如果从缓存加载已就绪，直接切换
      if (back.readyState >= 3) {
        back.removeEventListener('canplay', switchBuffer);
        back.removeEventListener('error', onError);
        switchBuffer();
      }
      // 超时兜底：5 秒后如果遮罩还在，强制解除
      const timeout = setTimeout(() => { if (!cancelled) setSwitchLoading(false); }, 5000);
      return () => {
        cancelled = true;
        clearTimeout(timeout);
        setSwitchLoading(false);
        back.removeEventListener('canplay', switchBuffer);
        back.removeEventListener('error', onError);
      };
    }
    return () => {
      cancelled = true;
      setSwitchLoading(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewCurrentIndex, showPreview]);

  // 缩略图列表自动滚动到当前分镜
  useEffect(() => {
    const strip = thumbnailStripRef.current;
    if (!strip) return;
    const child = strip.children[previewCurrentIndex] as HTMLElement | undefined;
    if (child) {
      child.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [previewCurrentIndex]);

  // Asset picker tabs config
  // 资产名称 → 类别标签查找表
  const assetCategoryMap = useMemo(
    () => getAssetCategoryMap(analysisAssets, {
      character: t('pickerModal.character'),
      scene: t('pickerModal.scene'),
      prop: t('pickerModal.prop'),
    }),
    [analysisAssets, t],
  );

  const pickerTabs: AssetPickerTab[] = [
    { key: 'characters', label: t('pickerModal.character'), elemType: 'character' },
    { key: 'scenes', label: t('pickerModal.scene'), elemType: 'scene' },
    { key: 'props', label: t('pickerModal.prop'), elemType: 'prop' },
    ...(projectData?.projectType === 'real_person' && platformSupportsResourceReview(projectData?.videoPlatform) && projectActiveReviews.length > 0
      ? [{ key: 'reviewed' as const, label: t('pickerModal.reviewed'), elemType: 'reviewed' }] : []),
    { key: 'library' as const, label: t('pickerModal.library'), elemType: 'library' },
    { key: 'shotVideos' as const, label: t('pickerModal.shotVideos'), elemType: 'shotVideos' },
  ];

  if (loading) {
    return (
      <div className="container episode-detail-page">
        <div className="empty-state">
          <div className="loading-spinner" style={{ width: '32px', height: '32px', borderWidth: '3px' }}></div>
          <p>{t("loading")}</p>
        </div>
      </div>
    );
  }

  if (!episode) {
    return (
      <div className="container episode-detail-page">
        <EmptyState icon="❓" title={t("episodeNotFound")} />
      </div>
    );
  }

  return (
    <>
    <div className="container episode-detail-page">
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
        {!isMobile && episodeSidebar && (
          <aside style={{
            position: 'sticky', top: 'calc(var(--header-height) + 1rem)', width: '170px', flexShrink: 0, zIndex: 20,
            maxHeight: 'calc(100vh - var(--header-height) - 2rem)', display: 'flex', flexDirection: 'column',
          }}>
            {episodeSidebar}
          </aside>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          {isMobile && episodeSidebar && (
            <button
              onClick={() => setEpisodeDrawerOpen(true)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', marginBottom: '0.75rem',
                background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '8px',
                padding: '6px 12px', fontSize: '0.85rem', color: 'var(--text-2)', cursor: 'pointer',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" />
              </svg>
              {t('episodes.title', { ns: 'projectDetail' })}
            </button>
          )}
      <EpisodePageHeader
        episode={episode}
        projectName={projectName}
        polling={polling}
        t={t}
        headerDuration={showPreview ?
          <span style={{ fontSize: '0.85rem', fontWeight: 400, color: 'var(--text-2)' }}>
            {previewableShots.reduce((sum, shot) => sum + (shot.duration || 0), 0)}{t('preview.durationSuffix')}
          </span>
        : undefined}
        headerActions={<>
          <AIClipControls
            key={`ai-clip-${episode.id}`}
            episodeId={episode.id}
            gateReady={isEpisodeClipReady(shots, shotVersions)}
          />
          <DirectorMappingControls
            key={episode.id}
            episode={episode}
            variant="inline"
            refreshToken={directorMappingRefreshToken}
            onStateChange={handleDirectorMappingStateChange}
            onTerminal={handleDirectorMappingTerminal}
          />
          {showPreview && (
            <button
              className="btn btn-secondary"
              style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
              onClick={handleTogglePreview}
            >
              {t('preview.segments')}
            </button>
          )}
        </>}
      />

      {/* 视频预览拼接播放弹窗：保持挂载用 display 切换（video 双缓冲 ref 依赖持续挂载），
          portal 到 body 防容器裁剪 */}
      {showPreview && previewMounted && ReactDOM.createPortal(
        <div
          style={{
            display: previewExpanded ? 'flex' : 'none',
            position: 'fixed', inset: 0, zIndex: 1300,
            background: 'rgba(0, 0, 0, 0.55)',
            alignItems: 'center', justifyContent: 'center', padding: '1.5rem',
          }}
          onClick={handleTogglePreview}
        >
          <div
            style={{
              background: 'var(--surface)', borderRadius: '12px',
              width: 'min(960px, 94vw)', maxHeight: '92vh', overflow: 'auto',
              padding: '1rem 1.25rem 1.25rem',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.3)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
              <strong style={{ fontSize: '1rem' }}>{t('preview.segments')}</strong>
              <span style={{ fontSize: '0.85rem', color: 'var(--text-2)' }}>
                {previewableShots.reduce((sum, shot) => sum + (shot.duration || 0), 0)}{t('preview.durationSuffix')}
              </span>
              <span style={{ flex: 1 }} />
              <button
                className="btn btn-secondary"
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                disabled={!!downloadProgress}
                onClick={handleDownloadAll}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: '-2px' }}>
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                {downloadProgress ? t('preview.downloading', { current: downloadProgress.current, total: downloadProgress.total }) : t('preview.downloadAll')}
              </button>
              <button
                className="btn btn-secondary"
                style={{ fontSize: '0.8rem', padding: '0.3rem 0.75rem' }}
                onClick={() => { setShareForm({ hours: 12, maxViews: 0 }); setShareModal(true); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px', verticalAlign: '-2px' }}>
                  <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
                  <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
                </svg>
                {t('preview.share')}
              </button>
              <button
                className="btn btn-ghost"
                aria-label={t('close', { ns: 'common', defaultValue: '关闭' })}
                style={{ fontSize: '1rem', padding: '0.2rem 0.6rem' }}
                onClick={handleTogglePreview}
              >
                ✕
              </button>
            </div>
            <EpisodePreviewPlayer
              previewableShots={previewableShots}
              previewCurrentIndex={previewCurrentIndex}
              activeBuffer={activeBuffer}
              initialReady={initialReady}
              switchLoading={switchLoading}
              videoRefA={videoRefA}
              videoRefB={videoRefB}
              thumbnailStripRef={thumbnailStripRef}
              t={t}
              getSelectedVideoURL={getSelectedVideoURL}
              onPreviewEnded={handlePreviewEnded}
              onPreviewPlayingChange={setPreviewPlaying}
              onPreviewCurrentIndexChange={setPreviewCurrentIndex}
              onPlay={handlePreviewPlay}
            />
          </div>
        </div>,
        document.body,
      )}

      {episodeSummary && (
        <EpisodeSummaryCard
          key={episode.id}
          summary={episodeSummary}
          scriptView={
            <EpisodeScriptPeek
              episode={episode}
              content={content}
              originalContent={originalContent}
              originalContentLoaded={originalContentLoaded}
              normalizedContent={normalizedContent}
              normalizedContentLoaded={normalizedContentLoaded}
              translations={translations}
              transContent={transContent}
              onLoadOriginal={() => { if (id) loadOriginalContent(id); }}
              onLoadNormalized={() => { if (id) loadNormalizedContent(id); }}
              onLoadTranslation={loadTranslationContent}
              memory={scriptPeekMemory}
            />
          }
        />
      )}

      {/* 分镜列表 */}
      <div className="card theme-left-border" style={{ borderRadius: '10px', '--_theme-color': 'var(--theme-storyboard)', '--_theme-color-end': '#3b82f6' } as React.CSSProperties}>
        <ShotListHeader
          episode={episode}
          shotsCount={shots.length}
          polling={polling}
          analysisCompleted={analysisCompleted}
          enableScriptNormalization={!!projectData?.enableScriptNormalization}
          rematchingEpisodeAssets={rematchingEpisodeAssets}
          assetReviewRequired={assetReviewRequired}
          assetReviewAllActive={assetReviewAllActive}
          submittingReview={submittingReview}
          generatingPrompts={generatingPrompts}
          normalizingEpisode={normalizingEpisode}
          resolvingPronouns={resolvingPronouns}
          t={t}
          onConfirmRematchAssets={() => setConfirmRematchAssets(true)}
          onSubmitAssetReview={handleSubmitAssetReview}
          onGeneratePrompts={handleGeneratePrompts}
          onNormalize={handleNormalize}
          onResolvePronouns={handleResolvePronouns}
        />

        {/* 审核状态面板（人物审核 + 素材审核合并，保留素材提交流程） */}
        <ReviewStatusPanel
          assetReviews={assetReviews}
          assetReviewRequired={assetReviewRequired}
          assetReviewAllActive={assetReviewAllActive}
          resourceReviews={resourceReviews}
          resourceReviewRequired={resourceReviewRequired}
          collapsed={reviewPanelCollapsed}
          onToggleCollapsed={() => setReviewPanelCollapsed(prev => !prev)}
          onSubmitResource={handleSubmitResourceReview}
          submittingResource={submittingResourceReview}
        />

        {shots.length === 0 ? (
          <EmptyState icon="🎬" title={t('shotList.noShots')} description={t('shotList.noShotsHint')} />
        ) : (
          <>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
          <ShotNavigationDock
            episodeIndex={episode.index}
            shots={shots}
            shotVersions={shotVersions}
            activeShotId={activeShotId}
            shotWeights={shotWeights}
            playedVideos={playedVideos}
            subtitleEraseStatus={subtitleEraseStatus}
            prevEpisodeId={prevEpisodeId}
            nextEpisodeId={nextEpisodeId}
            t={t}
            onNavigateEpisode={(episodeId) => navigate(projectEpisodePath(episode.projectId, episodeId))}
          />
          <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '0' }}>
            {/* 在第一个分镜前的插入热区 */}
            <ShotInsertZone
              title={t('shotList.insertFront')}
              onClick={() => { setInsertAfterIndex(0); setInsertShotData({ duration: 5, prompt: '' }); }}
            />
            {shots.map((shot) => (
              <React.Fragment key={shot.id}>
              <StatusFlash status={shot.status} id={`shot-${shot.id}`} style={{ borderRadius: 'var(--radius)', scrollMarginTop: 'calc(var(--header-height) + 16px)', boxShadow: '0 2px 12px rgba(0,0,0,0.07), 0 0 1px rgba(0,0,0,0.08)', background: 'var(--surface)' }}>
                {/* Shot 头部（点击展开） */}
                <ShotHeader
                  shot={shot}
                  versions={shotVersions[shot.id] || []}
                  collapsed={collapsedShots.has(shot.id)}
                  t={t}
                  onExpand={() => handleExpandShot(shot.id)}
                />

                {/* Shot 详情 */}
                <div style={{
                  maxHeight: !collapsedShots.has(shot.id) ? '5000px' : '0',
                  opacity: !collapsedShots.has(shot.id) ? 1 : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.25s ease, opacity 0.25s ease',
                }}>
                  <div style={{ padding: '1.25rem', background: 'var(--surface-2)', borderRadius: '0 0 var(--radius) var(--radius)' }}>
                    <ShotDetails
                      shot={shot}
                      selectedVideoUrl={getSelectedVideoURL(shot.id)}
                      aspectRatio={projectData?.aspectRatio}
                      languageSkillId={projectData?.languageSkillId}
                      languageSkillName={languageSkillName}
                      minDuration={videoLimits.limits.minDurationSec}
                      maxDuration={videoLimits.limits.maxDurationSec}
                      applyingLanguage={applyingLanguage === shot.id}
                      rematching={rematchingAssets === shot.id}
                      generating={Boolean(pendingVideoSubmissions[shot.id])}
                      resourceReviews={resourceReviews}
                      credits={getShotCredits(shot)}
                      refs={getAssetRefs(shot)}
                      resources={shotResources[shot.id] || []}
                      uploadingResource={uploadingResource === shot.id}
                      assetCategoryMap={assetCategoryMap}
                      projectId={episode.projectId}
                      t={t}
                      editing={editingShot === shot.id}
                      savingShot={savingShot}
                      shotEdits={shotEdits}
                      onShotEditsChange={setShotEdits}
                      onSave={() => handleSaveShot(shot.id)}
                      onCancel={() => setEditingShot(null)}
                      onSaveAndGenerate={() => handleSaveAndGenerate(shot)}
                      onDelete={() => { setDeleteShotTarget({ id: shot.id, index: shot.index }); setDeleteShotStep(1); }}
                      onDuplicate={() => setDuplicateShotTarget({ ...shot, resources: shotResources[shot.id] || [] })}
                      onEdit={() => handleEditShot(shot)}
                      onRematchAssets={() => handleRematchAssets(shot.id)}
                      onApplyLanguage={() => handleApplyLanguage(shot.id)}
                      onGenerateVideo={() => handleGenerateVideoClick(shot)}
                      onPreviewAsset={setPreviewAsset}
                      onRemoveAssetRef={(imgIndex) => handleRemoveAssetRef(shot.id, imgIndex)}
                      onDeleteResource={(resourceId) => setConfirmDeleteResource({ resourceId, shotId: shot.id })}
                      onUploadResource={() => handleUploadButtonClick(shot.id)}
                      onPickAsset={() => { setPickerSelected([]); setPickingAssetFor(shot.id); }}
                      onSwitchState={(ref) => {
                        const found = findAssetLookupEntry(assetLookup, ref);
                        const assetType = found?.type && found.type !== 'reviewed' ? found.type : null;
                        setStateSelectorData({ shotId: shot.id, imgIndex: ref.imgIndex, assetName: ref.name, assetType, allStates: ref.allStates, currentState: ref.state, currentImageUrl: ref.imageUrl });
                      }}
                      onOpenComments={() => {
                        setCommentShotId(shot.id);
                        setCommentShotIndex(shot.index);
                      }}
                      onPreviewPlayed={() => {
                        // 大预览观看也算已看：标记当前显示的版本（擦除版用 erased: 前缀 id）
                        const sel = selectedVideoVersions[shot.id];
                        const playedId = sel && sel.startsWith('erased:')
                          ? sel
                          : getSelectedVersionId(shot.id);
                        if (playedId) markVideoPlayed(playedId);
                      }}
                    />
                  </div>
                </div>

                {/* 生成历史 - 无需展开分镜即可查看 */}
                {(shotVersions[shot.id] || []).length > 0 && (
                  <div style={{ padding: '0.75rem 1.25rem', background: 'var(--surface-2)', borderRadius: !collapsedShots.has(shot.id) ? '0' : '0 0 var(--radius) var(--radius)' }}>
                    <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-2)' }}>
                      {t('version.historyTitle', { count: shotVersions[shot.id].length })}
                    </p>
                    <div style={{ display: 'flex', gap: '0.75rem', overflowX: 'auto', paddingBottom: '0.75rem' }}>
                      {(() => {
                      const versionsForShot = shotVersions[shot.id];
                      const fallbackPrimaryId = [...versionsForShot].reverse().find(sv => sv.taskStatus === 'completed' && sv.videoURL)?.id;
                      return versionsForShot.map((v) => {
                        const isPrimary = selectedVideoVersions[shot.id] === v.id
                          || (!selectedVideoVersions[shot.id] && v.id === fallbackPrimaryId);
                        const isSecondary = !isPrimary && (secondaryVersions[shot.id] || []).includes(v.id);
                        const borderColor = isPrimary ? 'var(--theme-storyboard)' : isSecondary ? 'var(--theme-storyboard)' : 'var(--border)';
                        const erasedId = `erased:${v.id}`;
                        const hasErasedVideo = subtitleEraseStatus[v.id]?.status === 'completed' && subtitleEraseStatus[v.id]?.resultVideoUrl;
                        const isErasedPrimary = selectedVideoVersions[shot.id] === erasedId;
                        const isErasedSecondary = (secondaryVersions[shot.id] || []).includes(erasedId);
                        const erasedBorderColor = isErasedPrimary ? 'var(--theme-storyboard)' : isErasedSecondary ? 'var(--theme-storyboard)' : 'var(--border)';
                        return (
                        <React.Fragment key={v.id}>
                        <div
                          ref={el => { if (el && isPrimary && !scrolledShotsRef.current.has(shot.id)) { scrolledShotsRef.current.add(shot.id); requestAnimationFrame(() => { const parent = el.parentElement; if (parent) { parent.scrollLeft = el.offsetLeft - parent.offsetWidth / 2 + el.offsetWidth / 2; } }); } }}
                          style={{ flexShrink: 0, width: '240px', borderRadius: 'var(--radius-sm)', border: isPrimary ? `3px solid ${borderColor}` : 'none', overflow: 'hidden', background: 'var(--surface)', boxShadow: isPrimary ? 'none' : '0 1px 6px rgba(0,0,0,0.05), 0 0 1px rgba(0,0,0,0.06)' }}
                        >
                          <ShotVersionPreview
                            version={v}
                            isPrimary={isPrimary}
                            isSecondary={isSecondary}
                            showVersionActions={showVersionActions}
                            played={playedVideos.has(v.id)}
                            cancelFailed={cancelFailedTaskIds.has(v.videoTaskId || '')}
                            canceling={cancelingVideo === v.id}
                            active={activatedVersions.has(v.id)}
                            aspectRatio={projectData?.aspectRatio}
                            onActivate={() => activateVersion(v.id)}
                            t={t}
                            onPlayed={() => markVideoPlayed(v.id)}
                            onToggleSecondary={() => {
                              toggleSecondaryVersion(shot.id, v.id, isSecondary);
                            }}
                            onSelectPrimary={() => {
                              selectPrimaryVersion(shot.id, v.id);
                            }}
                            onCancel={() => handleCancelVideo(shot.id, v.id, v.videoTaskId || '')}
                          />
                          <div style={{ padding: '0.5rem' }}>
                            <ShotVersionHeader
                              version={v}
                              t={t}
                              onDownload={async () => {
                                const idx = String(shot.index).padStart(2, '0');
                                const fileName = t('download.filenameVersion', { project: projectName || t('download.fallbackProject'), episode: episode?.index ?? 1, shot: idx, version: v.version });
                                try {
                                  const { downloadUrl } = await getDownloadUrl(v.videoURL!, fileName);
                                  window.open(downloadUrl, '_blank');
                                } catch {
                                  window.open(v.videoURL!, '_blank');
                                }
                              }}
                            />
                            <SubtitleEraseStatusNotice task={subtitleEraseStatus[v.id]} t={t} />
                            {v.taskStatus === 'completed' && v.videoURL && (() => {
                              const _extracting = extractingVersionIds.has(v.id) || (extractionLoading && extractionModal?.versionId === v.id);
                              const _extracted = extractedVersionIds.has(v.id);
                              const _swapStatus = swapVersionStatus[v.id];
                              return (
                                <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
                                  {canEraseSubtitle(v) && !subtitleEraseStatus[v.id] && (
                                    <button onClick={() => handleSubtitleErase(v.id)} style={{ background: 'none', border: '1px solid #8b5cf6', borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: '#8b5cf6' }}>
                                      {t('version.eraseSubtitle')}
                                    </button>
                                  )}
                                  {subtitleEraseStatus[v.id]?.status === 'failed' && canEraseSubtitle(v) && (
                                    <button onClick={() => handleSubtitleErase(v.id)} style={{ background: 'none', border: '1px solid #8b5cf6', borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: '#8b5cf6' }}>
                                      {t('version.reEraseSubtitle')}
                                    </button>
                                  )}
                                  <button onClick={() => handleExtractVoices(shot.id, v.id)} disabled={_extracting} style={{ background: 'none', border: `1px solid ${_extracted ? '#16a34a' : 'var(--primary)'}`, borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: _extracted ? '#16a34a' : 'var(--primary)' }}>
                                    {_extracting ? t('version.extractingVoice') : _extracted ? t('version.viewVoice') : t('version.extractVoice')}
                                  </button>
                                  {_swapStatus?.status === 'pending' || _swapStatus?.status === 'processing' || _swapStatus?.status === 'waiting' ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem', color: '#ea580c' }}>
                                      <span className="loading-spinner" style={{ width: '12px', height: '12px', borderWidth: '2px' }}></span>
                                      {t('version.replacingVoice')}
                                    </span>
                                  ) : _swapStatus?.status === 'completed' ? (
                                    <button onClick={() => { setSwapTask({ replacedVocalUrl: _swapStatus.replacedVocalUrl, bgmUrl: _swapStatus.bgmUrl, matchedSegments: _swapStatus.matchedSegments, status: 'completed' }); setSwapModal({ shotId: shot.id, versionId: v.id }); }} style={{ background: 'none', border: '1px solid #16a34a', borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: '#16a34a' }}>
                                      {t('version.viewNewVoice')}
                                    </button>
                                  ) : _swapStatus?.status === 'failed' ? (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                      <span style={{ fontSize: '0.72rem', color: '#dc2626' }}>{_swapStatus.errorMessage ? t('version.swapFailedWithMsg', { msg: _swapStatus.errorMessage }) : t('version.swapFailed')}</span>
                                      <button onClick={() => handleOpenSwap(shot.id, v.id)} style={{ background: 'none', border: '1px solid #ea580c', borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: '#ea580c' }}>
                                        {t('version.reSwapVoice')}
                                      </button>
                                    </div>
                                  ) : (
                                    <button onClick={() => handleOpenSwap(shot.id, v.id)} style={{ background: 'none', border: '1px solid #ea580c', borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: '#ea580c' }}>
                                      {t('version.swapVoice')}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => setClipTarget({ shotId: shot.id, versionId: v.id, videoURL: v.videoURL! })}
                                    style={{ background: 'none', border: '1px solid #0891b2', borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: '#0891b2' }}
                                  >
                                    {t('version.clipVideo')}
                                  </button>
                                </div>
                              );
                            })()}
                            {voiceError[v.id] && (
                              <div style={{ marginBottom: '0.35rem', padding: '0.35rem 0.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', fontSize: '0.72rem', color: '#dc2626', wordBreak: 'break-all' }}>
                                {voiceError[v.id]}
                              </div>
                            )}
                            <ShotVersionDetailPanel
                              version={v}
                              expanded={Boolean(showVersionDetail[v.id])}
                              t={t}
                              onToggle={() => setShowVersionDetail(prev => ({ ...prev, [v.id]: !prev[v.id] }))}
                            />
                          </div>
                        </div>
                        {hasErasedVideo && (
                          <div style={{ flexShrink: 0, width: '240px', borderRadius: 'var(--radius-sm)', border: isErasedPrimary ? `3px solid ${erasedBorderColor}` : 'none', overflow: 'hidden', background: 'var(--surface)', boxShadow: isErasedPrimary ? 'none' : '0 1px 6px rgba(0,0,0,0.05), 0 0 1px rgba(0,0,0,0.06)' }}>
                            <div style={{ position: 'relative' }}>
                              {activatedVersions.has(erasedId) ? (
                                <video
                                  src={tosAccelerate(subtitleEraseStatus[v.id].resultVideoUrl!)}
                                  controls
                                  preload="metadata"
                                  autoPlay
                                  style={{ width: '100%', aspectRatio: cssAspectRatio(projectData?.aspectRatio), maxHeight: '300px', objectFit: 'contain', display: 'block', background: '#000' }}
                                  onPlay={() => markVideoPlayed(erasedId)}
                                />
                              ) : (
                                <VideoLoadPlaceholder
                                  aspectRatio={projectData?.aspectRatio}
                                  label={t('version.loadVideo')}
                                  onActivate={() => activateVersion(erasedId)}
                                />
                              )}
                              {!playedVideos.has(erasedId) && (
                                <span style={{ position: 'absolute', top: '6px', left: '6px', background: '#22c55e', color: '#fff', fontSize: '0.6rem', fontWeight: 700, padding: '2px 5px', borderRadius: '3px', lineHeight: 1, zIndex: 3 }}>NEW</span>
                              )}
                              {showVersionActions && !isErasedPrimary && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); toggleSecondaryVersion(shot.id, erasedId, isErasedSecondary); }}
                                  style={{ position: 'absolute', top: '6px', right: '34px', background: isErasedSecondary ? 'var(--theme-storyboard)' : 'rgba(0,0,0,0.5)', color: '#fff', border: isErasedSecondary ? '2px solid #fff' : '2px solid rgba(255,255,255,0.6)', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', lineHeight: 1 }}
                                  title={isErasedSecondary ? t('version.removeSecondary') : t('version.secondaryTitle')}
                                >
                                  {isErasedSecondary ? '★' : '☆'}
                                </button>
                              )}
                              {showVersionActions && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); selectPrimaryVersion(shot.id, erasedId); }}
                                  style={{ position: 'absolute', top: '6px', right: '6px', background: isErasedPrimary ? 'var(--theme-storyboard)' : 'rgba(0,0,0,0.5)', color: '#fff', border: isErasedPrimary ? '2px solid #fff' : '2px solid rgba(255,255,255,0.6)', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', fontSize: '0.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }}
                                  title={isErasedPrimary ? t('version.isPrimary') : t('version.markPrimary')}
                                >
                                  ✓
                                </button>
                              )}
                            </div>
                            <div style={{ padding: '0.5rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                                <strong style={{ fontSize: '0.8rem' }}>{t('version.erasedVersion', { version: v.version })}</strong>
                                <div style={{ flex: 1 }} />
                                <button
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const idx = String(shot.index).padStart(2, '0');
                                    const fileName = t('download.filenameErased', { project: projectName || t('download.fallbackProject'), episode: episode?.index ?? 1, shot: idx, version: v.version });
                                    try {
                                      const { downloadUrl } = await getDownloadUrl(subtitleEraseStatus[v.id].resultVideoUrl!, fileName);
                                      window.open(downloadUrl, '_blank');
                                    } catch {
                                      window.open(subtitleEraseStatus[v.id].resultVideoUrl!, '_blank');
                                    }
                                  }}
                                  style={{ background: 'none', border: '1px solid var(--border)', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px', fontSize: '0.75rem', color: 'var(--text-2)' }}
                                  title={t('version.downloadVideo')}
                                >
                                  {t('version.downloadVideo')}
                                </button>
                              </div>
                              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                                <button
                                  onClick={() => setClipTarget({ shotId: shot.id, versionId: v.id, videoURL: subtitleEraseStatus[v.id].resultVideoUrl!, source: 'erased' })}
                                  style={{ background: 'none', border: '1px solid #0891b2', borderRadius: '4px', cursor: 'pointer', padding: '3px 10px', fontSize: '0.75rem', color: '#0891b2' }}
                                >
                                  {t('version.clipVideo')}
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                        </React.Fragment>
                        );
                      });
                      })()}
                    </div>
                  </div>
                )}
              </StatusFlash>
              {/* 在当前分镜后的插入热区 */}
              <ShotInsertZone
                title={t('shotList.insertAfter', { index: shot.index })}
                onClick={() => { setInsertAfterIndex(shot.index); setInsertShotData({ duration: 5, prompt: '' }); }}
              />
              </React.Fragment>
            ))}
          </div>
          </div>
          </>
        )}
      </div>

      {/* 上一集 / 下一集 导航 */}
      <EpisodeFooterNav
        show={episodeCount > 1}
        prevEpisodeId={prevEpisodeId}
        nextEpisodeId={nextEpisodeId}
        t={t}
        onNavigateEpisode={(episodeId) => navigate(projectEpisodePath(episode.projectId, episodeId))}
      />

      {/* 剧集原文 */}
      <EpisodeContentCard
        episode={episode}
        projectData={projectData}
        content={content}
        contentTab={contentTab}
        originalContent={originalContent}
        originalContentLoaded={originalContentLoaded}
        normalizedContent={normalizedContent}
        normalizedContentLoaded={normalizedContentLoaded}
        translations={translations}
        transContent={transContent}
        startableLanguages={startableLanguages}
        onStartTranslation={handleStartTranslation}
        editingContent={editingContent}
        editedContent={editedContent}
        savingContent={savingContent}
        normalizingEpisode={normalizingEpisode}
        resolvingPronouns={resolvingPronouns}
        t={t}
        onContentTabChange={(tab) => {
          setContentTab(tab);
          if (tab === 'original' && id) loadOriginalContent(id);
          if (tab === 'normalized' && id) loadNormalizedContent(id);
          if (tab.startsWith('trans:')) loadTranslationContent(tab.slice('trans:'.length));
        }}
        onStartEdit={() => { setEditedContent(originalContentLoaded ? originalContent : content); setEditingContent(true); }}
        onCancelEdit={() => setEditingContent(false)}
        onEditedContentChange={setEditedContent}
        onSaveContent={handleSaveContent}
        onNormalize={handleNormalize}
        onResolvePronouns={handleResolvePronouns}
      />
        </div>
      </div>
    </div>

    {isMobile && (
      <MobileAssetDrawer
        open={episodeDrawerOpen}
        title={t('episodes.title', { ns: 'projectDetail' })}
        onClose={() => setEpisodeDrawerOpen(false)}
      >
        {episodeSidebar}
      </MobileAssetDrawer>
    )}

    <ConfirmDialog
      open={confirmRegenAllOpen}
      onCancel={() => setConfirmRegenAllOpen(false)}
      onConfirm={handleRegenAllPrompts}
      title={t('confirmDialogs.regenAllPrompts.title', { ns: 'projectDetail' })}
      message={t('confirmDialogs.regenAllPrompts.title', { ns: 'projectDetail' })}
      confirmText={t('confirmDialogs.regenAllPrompts.confirm', { ns: 'projectDetail' })}
      confirmDanger
      loading={regenAllLoading}
    />
    <ConfirmDialog
      open={confirmNormalizeAllOpen}
      onCancel={() => setConfirmNormalizeAllOpen(false)}
      onConfirm={handleNormalizeAll}
      title={t('confirmDialogs.normalizeAll.title', { ns: 'projectDetail' })}
      message={t('confirmDialogs.normalizeAll.title', { ns: 'projectDetail' })}
      confirmText={t('confirmDialogs.normalizeAll.confirm', { ns: 'projectDetail' })}
      confirmDanger
      loading={normalizeAllLoading}
    />
    <ConfirmDialog
      open={confirmRetryFailedOpen}
      onCancel={() => setConfirmRetryFailedOpen(false)}
      onConfirm={handleRetryFailedPrompts}
      title={t('confirmDialogs.retryFailed.title', { ns: 'projectDetail' })}
      message={t('confirmDialogs.retryFailed.title', { ns: 'projectDetail' })}
      confirmText={t('confirmDialogs.retryFailed.confirm', { ns: 'projectDetail' })}
      confirmDanger
      loading={retryFailedLoading}
    />

    {projectData && (
      <>
        <EditProjectDialog
          cfg={projectCfg}
          open={editingProject}
          onClose={() => setEditingProject(false)}
          analysisStarted
        />
        <StyleConfigDialog
          cfg={projectCfg}
          projectId={projectData.id}
          open={styleConfigOpen}
          onClose={() => setStyleConfigOpen(false)}
        />
      </>
    )}
    <EpisodeDetailModals
      episode={episode}
      id={id}
      t={t}
      toast={toast}
      showNormalizePrompt={showNormalizePrompt}
      projectData={projectData}
      setShowNormalizePrompt={setShowNormalizePrompt}
      handleNormalize={handleNormalize}
      handleResolvePronouns={handleResolvePronouns}
      insertAfterIndex={insertAfterIndex}
      insertShotData={insertShotData}
      insertingShotLoading={insertingShotLoading}
      setInsertAfterIndex={setInsertAfterIndex}
      handleInsertShot={handleInsertShot}
      setInsertShotData={setInsertShotData}
      minDuration={videoLimits.limits.minDurationSec}
      maxDuration={videoLimits.limits.maxDurationSec}
      // 选择器的"还可添加 N 个"与添加按钮 disabled 用的总数上限。
      // resolved=false 时传 0 = 不限，让选择器不设上限（后端是唯一权威闸口）。
      maxRefTotal={videoLimits.resolved ? videoLimits.limits.maxRefTotal : 0}
      confirmRegenerate={confirmRegenerate}
      confirmRematchAssets={confirmRematchAssets}
      confirmDeleteResource={confirmDeleteResource}
      deleteShotTarget={deleteShotTarget}
      deleteShotStep={deleteShotStep}
      deletingShotLoading={deletingShotLoading}
      setConfirmRegenerate={setConfirmRegenerate}
      doGeneratePrompts={doGeneratePrompts}
      setConfirmRematchAssets={setConfirmRematchAssets}
      handleRematchEpisodeAssets={handleRematchEpisodeAssets}
      setConfirmDeleteResource={setConfirmDeleteResource}
      handleDeleteResource={handleDeleteResource}
      setDeleteShotTarget={setDeleteShotTarget}
      setDeleteShotStep={setDeleteShotStep}
      handleDeleteShot={handleDeleteShot}
      duplicateShotTarget={duplicateShotTarget}
      duplicatingShotLoading={duplicatingShotLoading}
      setDuplicateShotTarget={setDuplicateShotTarget}
      handleDuplicateShot={handleDuplicateShot}
      humanContentDialogOpen={humanContentDialogOpen}
      handleHumanDialogNoHuman={handleHumanDialogNoHuman}
      handleHumanDialogHasHuman={handleHumanDialogHasHuman}
      handleHumanDialogCancel={handleHumanDialogCancel}
      uploadFileInputRef={uploadFileInputRef}
      pendingUploadShotIdRef={pendingUploadShotIdRef}
      handleUploadResource={handleUploadResource}
      pickingAssetFor={pickingAssetFor}
      addingAssets={addingAssets}
      pickerTab={pickerTab}
      pickerTabs={pickerTabs}
      pickerSearch={pickerSearch}
      pickerSelected={pickerSelected}
      analysisAssets={analysisAssets}
      refreshAnalysisAssets={refreshAnalysisAssets}
      projectActiveReviews={projectActiveReviews}
      shots={shots}
      shotResources={shotResources}
      getAssetRefs={getAssetRefs}
      setPickingAssetFor={setPickingAssetFor}
      setPickerSelected={setPickerSelected}
      setPickerSearch={setPickerSearch}
      setPickerTab={setPickerTab}
      togglePickerAsset={togglePickerAsset}
      handleAddAssetRefs={handleAddAssetRefs}
      projectAssets={projectAssets}
      librarySelectedIds={librarySelectedIds}
      setLibrarySelectedIds={setLibrarySelectedIds}
      handleAddLibraryAssets={handleAddLibraryAssets}
      addingVideoRef={addingVideoRef}
      shotVersions={shotVersions}
      selectedVideoVersions={selectedVideoVersions}
      ineligibleLabel={ineligibleLabel}
      ineligibleTitle={ineligibleTitle}
      handleAddVideoRef={handleAddVideoRef}
      stateSelectorData={liveStateSelectorData}
      setStateSelectorData={setStateSelectorData}
      handleSwitchAssetRef={handleSwitchAssetRef}
      previewAsset={previewAsset}
      setPreviewAsset={setPreviewAsset}
      shareModal={shareModal}
      shareForm={shareForm}
      shareLoading={shareLoading}
      setShareModal={setShareModal}
      setShareForm={setShareForm}
      setShareLoading={setShareLoading}
      createShareLink={createShareLink}
      extractionModal={extractionModal}
      extractionTask={extractionTask}
      extractionLoading={extractionLoading}
      applyingVoice={applyingVoice}
      appliedVoices={appliedVoices}
      setExtractionModal={setExtractionModal}
      handleApplyVoice={handleApplyVoice}
      swapModal={swapModal}
      swapTask={swapTask}
      swapCharacters={swapCharacters}
      swapLoading={swapLoading}
      analysisChars={analysisChars}
      setSwapModal={setSwapModal}
      setSwapTask={setSwapTask}
      setSwapCharacters={setSwapCharacters}
      handleSwapVoice={handleSwapVoice}
      warnShot={warnShot}
      languageSkillName={languageSkillName}
      setWarnShot={setWarnShot}
      handleApplyLanguage={handleApplyLanguage}
      handleGenerateVideo={handleGenerateVideo}
    />
    <ClipCaptureModal
      open={!!clipTarget}
      target={clipTarget}
      isRealPersonReviewable={projectData?.projectType === 'real_person' && platformSupportsResourceReview(projectData?.videoPlatform)}
      t={t}
      onClose={() => setClipTarget(null)}
      onCaptured={(destination) => {
        if (destination === 'library') {
          toast.success(t('clipModal.addedToLibrary'));
          loadProjectAssets(); // spec：进 library 后刷新素材库，下次打开 picker 立即是新数据
        } else {
          toast.success(t('clipModal.sentToReview'));
          loadResourceReviewStatus();
        }
        setClipTarget(null);
      }}
      onError={(message) => toast.error(message)}
    />
    {commentShotId && (
      <ShotCommentModal
        shotId={commentShotId}
        shotIndex={commentShotIndex}
        t={t}
        onClose={() => setCommentShotId(null)}
        onChanged={() => {
          if (id) loadShots(id);
          // 评论新增/处理后，立即刷新左侧分集列表，让对应分集的未处理评论红点即时出现/消失。
          if (episode?.projectId) loadEpisodeList(episode.projectId);
          // 通知顶栏「视频创作」红点重新聚合未处理分镜评论。
          window.dispatchEvent(new Event('shot-comments-updated'));
        }}
      />
    )}
    </>
  );
};

export default EpisodeDetail;
